import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  FiUpload, FiX, FiFile, FiChevronDown, FiChevronRight,
  FiPlus, FiTrash2, FiInfo, FiZap, FiCalendar, FiDollarSign,
  FiFileText, FiTag, FiShield, FiClock, FiCheckCircle, FiCheck,
  FiArrowLeft, FiLayers, FiGrid, FiSearch, FiMapPin, FiPhone, FiMail, FiUser, FiBriefcase,
  FiSave, FiCornerDownLeft, FiPaperclip, FiSend, FiLock, FiBookOpen, FiDownload
} from 'react-icons/fi';
import { createContract, updateContract } from '../../store/slices/contractsSlice';
import { contractsAPI, documentsAPI, parametersAPI, departmentsAPI, contractTemplatesAPI, obligationsAPI, usersAPI, workflowsAPI, companiesAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { localizeSeeded } from '../FlowManagement/flowUtils';
import DateField from '../../components/DateField';
import SearchableSelect from '../../components/SearchableSelect';
import MoneyInput from '../../components/MoneyInput';
import { formatMoney } from '../../utils/numberFormat';
import { sanitizeFieldSchema } from '../../utils/sanitizeSchema';
import { getLocalized, localizedNames } from '../../i18n/getLocalized';
import { missingPermissionMessage, hasAnyPermission } from '../../utils/permissions';
import { TEMPLATE_LIBRARY_ENABLED } from '../../config/features';
import TemplateLibraryPicker from '../../components/TemplateLibraryPicker';
import ContractTemplatePicker from '../../components/ContractTemplatePicker';
import { saveBlob } from '../../utils/fileDownload';
import './ContractForm.scss';

// Initials for the counterparty avatar (e.g. "Acme Corp" → "AC").
function cpInitials(name) {
  if (!name) return '—';
  return name.trim().split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase();
}

// ─── Smart date parser — handles multiple formats and languages ─────────────
const MONTH_NAMES = {
  // English
  january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
  july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
  jan: 0, feb: 1, mar: 2, apr: 3, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
  // Turkish
  ocak: 0, şubat: 1, mart: 2, nisan: 3, mayıs: 4, haziran: 5,
  temmuz: 6, ağustos: 7, eylül: 8, ekim: 9, kasım: 10, aralık: 11,
  // German
  januar: 0, februar: 1, märz: 2, mai: 4, juni: 5, juli: 6,
  dezember: 11, okt: 9, dez: 11,
  // French
  janvier: 0, février: 1, mars: 2, avril: 3, juin: 5, juillet: 6,
  août: 7, septembre: 8, octobre: 9, novembre: 10, décembre: 11,
  // Spanish
  enero: 0, febrero: 1, marzo: 2, abril: 3, mayo: 4, junio: 5,
  julio: 6, agosto: 7, septiembre: 8, octubre: 9, noviembre: 10, diciembre: 11,
  // Arabic
  يناير: 0, فبراير: 1, مارس: 2, أبريل: 3, مايو: 4, يونيو: 5,
  يوليو: 6, أغسطس: 7, سبتمبر: 8, أكتوبر: 9, نوفمبر: 10, ديسمبر: 11,
};

function smartParseDate(str) {
  if (!str || typeof str !== 'string') return null;
  const s = str.trim();

  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  // ISO format: 2026-04-15T...
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.split('T')[0];

  // Try native Date.parse for unambiguous formats
  // But first, try our own parsing for more control

  // DD/MM/YYYY or DD-MM-YYYY or DD.MM.YYYY (day first — international standard)
  const dmyMatch = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (dmyMatch) {
    const [, d, m, y] = dmyMatch;
    if (+m >= 1 && +m <= 12 && +d >= 1 && +d <= 31) {
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
  }

  // MM/DD/YYYY — only if month <= 12 and day > 12 (unambiguous US format)
  const mdyMatch = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (mdyMatch) {
    const [, a, b, y] = mdyMatch;
    if (+a <= 12 && +b > 12 && +b <= 31) {
      return `${y}-${a.padStart(2, '0')}-${b.padStart(2, '0')}`;
    }
  }

  // YYYY/MM/DD
  const ymdMatch = s.match(/^(\d{4})[\/\-.](\d{1,2})[\/\-.](\d{1,2})$/);
  if (ymdMatch) {
    const [, y, m, d] = ymdMatch;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  // Named month: "April 4, 2026", "4 April 2026", "15 Nisan 2026", "décembre 3, 2026"
  const lower = s.toLowerCase().replace(/,/g, '').replace(/\s+/g, ' ').trim();
  for (const [name, monthIdx] of Object.entries(MONTH_NAMES)) {
    if (!lower.includes(name)) continue;
    // Extract numbers from the string
    const nums = lower.replace(name, '').match(/\d+/g);
    if (!nums || nums.length < 2) continue;
    let day, year;
    if (nums.length === 2) {
      // Determine which is day and which is year
      const [a, b] = nums.map(Number);
      if (a > 31) { year = a; day = b; }
      else if (b > 31) { year = b; day = a; }
      else { day = a; year = b; } // ambiguous, assume day first
    } else {
      // Take first small number as day, first large as year
      day = nums.find(n => +n <= 31);
      year = nums.find(n => +n > 31);
    }
    if (day && year) {
      const y = String(year).length === 2 ? `20${year}` : String(year);
      const m = String(monthIdx + 1).padStart(2, '0');
      const d = String(day).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }

  // Last resort: try native Date parsing
  const nativeDate = new Date(s);
  if (!isNaN(nativeDate.getTime()) && nativeDate.getFullYear() > 1900) {
    return nativeDate.toISOString().split('T')[0];
  }

  return null;
}

function parseFormattedNumber(str) {
  if (!str || typeof str !== 'string') return null;
  // Remove currency symbols, spaces, and letter chars
  let cleaned = str.replace(/[^\d.,\-]/g, '').trim();
  if (!cleaned) return null;
  // Handle European format: 250.000,00 → 250000.00
  if (/^\d{1,3}(\.\d{3})+,\d{1,2}$/.test(cleaned)) {
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  }
  // Handle US/standard format: 250,000.00 → 250000.00
  else if (/,/.test(cleaned)) {
    cleaned = cleaned.replace(/,/g, '');
  }
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

// ─── Template Results (mapping review) Modal ────────────────────────────────
function TemplateResultsModal({ open, results, onClose, onApply }) {
  const { t } = useTranslation();
  if (!open || !results) return null;
  return (
    <div className="cf-tmatch-overlay" onClick={onClose}>
      <div className="cf-tmatch" onClick={e => e.stopPropagation()}>
        <div className="cf-tmatch__header">
          <h2>{t('contractForm.templateFill.resultsTitle', { defaultValue: 'Extraction Results' })}</h2>
          <button className="cf-tmatch__close" onClick={onClose}><FiX size={18} /></button>
        </div>

        <div className="cf-tmatch__body">
          <div className="cf-tmatch__results">
            <div className="cf-tmatch__results-summary">
              <span className="cf-tmatch__results-matched">
                <FiCheckCircle size={14} />
                {t('contractForm.templateFill.matchedSummary', {
                  matched: results.matchedCount,
                  total: results.totalMappings,
                  defaultValue: '{{matched}} of {{total}} fields matched',
                })}
              </span>
            </div>
            <div className="cf-tmatch__results-list">
              {Object.values(results.extractedFields).map(field => (
                <div
                  key={field.fieldKey}
                  className={`cf-tmatch__result-item ${field.matched ? 'cf-tmatch__result-item--matched' : 'cf-tmatch__result-item--unmatched'}`}
                >
                  <div className="cf-tmatch__result-label">{field.fieldLabel}</div>
                  <div className="cf-tmatch__result-value">
                    {field.matched ? (
                      <>
                        <span>{field.value}</span>
                        <span className={`cf-tmatch__confidence cf-tmatch__confidence--${field.confidence}`}>
                          {field.confidence}
                        </span>
                      </>
                    ) : (
                      <span className="cf-tmatch__no-match">{t('contractForm.templateFill.notMatched', { defaultValue: 'Not matched' })}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="cf-tmatch__footer">
          <button className="cf-tmatch__btn cf-tmatch__btn--ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button className="cf-tmatch__btn cf-tmatch__btn--primary" onClick={onApply}>
            <FiZap size={14} />
            {t('contractForm.templateFill.applyToForm', { defaultValue: 'Apply to Form' })}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Fill-from-Template Widget (inline, in the page header) ──────────────────
// A modern replacement for the old "auto-fill" button. Pick a template in the
// picker modal (searchable, filtered by contract type / company / main group),
// download its .docx to fill in Word, then drop the filled copy back here. As
// soon as a template and a document are both present the document is matched
// against the template and the mapping review opens.
function TemplateFillWidget({ onApply }) {
  const { t } = useTranslation();
  // The chosen template is held whole, not by id: the strip shows its name and
  // the download uses its file, and the picker owns the list now.
  const [template, setTemplate] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [matching, setMatching] = useState(false);
  const [results, setResults] = useState(null);

  const templateId = template?._id || '';

  const runMatch = async (f, tid) => {
    if (!f || !tid) return;
    setMatching(true);
    try {
      const formData = new FormData();
      formData.append('document', f);
      const res = await contractTemplatesAPI.match(tid, formData);
      setResults(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || t('contractForm.templateFill.matchFailed', { defaultValue: 'Matching failed' }));
      setFile(null);
    } finally {
      setMatching(false);
    }
  };

  const chooseTemplate = (tpl) => {
    setTemplate(tpl);
    if (file && tpl?._id) runMatch(file, tpl._id);
  };

  // Hand the user the template's own .docx: the flow is download → fill in
  // Word → drop the filled copy back on the strip.
  const downloadTemplate = async () => {
    if (!template) return;
    setDownloading(true);
    try {
      const res = await contractTemplatesAPI.download(template._id);
      saveBlob(res.data, template.originalDocument?.originalName || `${template.name}.docx`);
    } catch {
      toast.error(t('contractForm.templateFill.downloadFailed', { defaultValue: 'Download failed' }));
    } finally {
      setDownloading(false);
    }
  };
  const chooseFile = (f) => {
    if (!f) return;
    if (!templateId) { toast.info(t('contractForm.templateFill.selectFirst', { defaultValue: 'Select a template first' })); return; }
    setFile(f);
    runMatch(f, templateId);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) chooseFile(f);
  };

  const applyResults = () => {
    if (results?.extractedFields) onApply(results.extractedFields, results.fieldSchemaId);
    setResults(null);
    setFile(null);
  };
  const closeResults = () => { setResults(null); setFile(null); };

  return (
    <div className="cf-tfill">
      <div className="cf-tfill__head">
        <span className="cf-tfill__badge"><FiZap size={13} /></span>
        <div className="cf-tfill__head-text">
          <span className="cf-tfill__title">{t('contractForm.autoFillFromTemplate')}</span>
          <span className="cf-tfill__sub">{t('contractForm.templateFill.note')}</span>
        </div>
      </div>

      <div className="cf-tfill__controls">
      <div className="cf-tfill__select">
        <button
          type="button"
          className={`cf-tfill__picker${template ? ' is-filled' : ''}`}
          onClick={() => setPickerOpen(true)}
          title={template ? template.name : undefined}
        >
          <span className="cf-tfill__picker-label">
            {template ? template.name : t('contractForm.templateFill.selectTemplate', { defaultValue: 'Select a template' })}
          </span>
          <FiChevronDown size={14} />
        </button>
      </div>

      <button
        type="button"
        className="cf-tfill__download"
        onClick={downloadTemplate}
        disabled={!template || downloading}
        aria-label={t('contractForm.templateFill.downloadTemplate', { defaultValue: 'Download template' })}
        title={template
          ? t('contractForm.templateFill.downloadTemplate', { defaultValue: 'Download template' })
          : t('contractForm.templateFill.selectFirst', { defaultValue: 'Select a template first' })}
      >
        {downloading ? <div className="cf-spinner cf-spinner--sm" /> : <FiDownload size={15} />}
        <span>{t('contractForm.templateFill.downloadTemplate', { defaultValue: 'Download template' })}</span>
      </button>

      <label
        // Idle it is an icon, like the download button beside it; it grows a
        // label only once it has something to report — the file it is holding,
        // or that a match is running.
        className={`cf-tfill__drop${dragOver ? ' is-drag' : ''}${!templateId ? ' is-disabled' : ''}${matching ? ' is-busy' : ''}${!file && !matching ? ' is-compact' : ''}`}
        // Where the wording lives now that the idle state carries no label —
        // and where a long file name stays readable in full.
        title={file ? file.name : (templateId
          ? t('contractForm.templateFill.dropHint', { defaultValue: 'Drop the document (.docx)' })
          : t('contractForm.templateFill.selectFirst', { defaultValue: 'Select a template first' }))}
        aria-label={templateId
          ? t('contractForm.templateFill.dropHint', { defaultValue: 'Drop the document (.docx)' })
          : t('contractForm.templateFill.selectFirst', { defaultValue: 'Select a template first' })}
        onDragOver={(e) => { if (templateId && !matching) { e.preventDefault(); setDragOver(true); } }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { if (templateId && !matching) onDrop(e); else e.preventDefault(); }}
      >
        {matching ? (
          <><div className="cf-spinner cf-spinner--sm" /><span>{t('contractForm.templateFill.matching', { defaultValue: 'Matching…' })}</span></>
        ) : file ? (
          <>
            <FiFileText size={16} />
            <span className="cf-tfill__file">{file.name}</span>
            <button type="button" className="cf-tfill__remove" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setFile(null); }}>
              <FiX size={13} />
            </button>
          </>
        ) : (
          // No label: the hint lives on the title/aria-label so the strip's
          // width goes to the guidance text instead.
          <FiUpload size={16} />
        )}
        <input
          type="file"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          style={{ display: 'none' }}
          disabled={!templateId || matching}
          onChange={(e) => { chooseFile(e.target.files[0]); e.target.value = ''; }}
        />
      </label>
      </div>

      <ContractTemplatePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={chooseTemplate}
        selectedId={templateId}
      />
      <TemplateResultsModal open={!!results} results={results} onClose={closeResults} onApply={applyResults} />
    </div>
  );
}

// ─── Core identity form initial state ────────────────────────────────────────
const INITIAL_CORE = {
  contractName: '',
  contractDate: new Date().toISOString().split('T')[0],
  contractTypeId: '',
  responsibleUserId: '',
  responsibleDepartmentId: '',
  companyId: '',
  counterpartyName: '',
  counterpartyId: '',
  counterpartyAddress: '',
  counterpartyCity: '',
  counterpartyCountry: '',
  counterpartyPhone: '',
  counterpartyEmail: '',
  counterpartyContactPerson: '',
  startDate: '',
  endDate: '',
  signingDate: '',
  reminderDays: '',
  autoRenewal: false,
  contractAmount: '',
  financialNote: '',
  currency: '',
  priority: 'medium',
  confidentialityLevel: 'internal',
  templateSource: '',
  // Recording an already-signed contract: unlocks the signed upload from the
  // first step and makes it mandatory before the contract can move on.
  isSignedContract: false,
  tags: '',
};

// Whose paper the draft came from.
const TEMPLATE_SOURCE_OPTIONS = [
  { value: 'internal', labelKey: 'contractForm.templateSources.internal' },
  { value: 'third_party', labelKey: 'contractForm.templateSources.thirdParty' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', labelKey: 'contractForm.priorities.low', color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
  { value: 'medium', labelKey: 'contractForm.priorities.medium', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
  { value: 'high', labelKey: 'contractForm.priorities.high', color: '#ef4444', bg: 'rgba(239,68,68,0.1)' },
  { value: 'critical', labelKey: 'contractForm.priorities.critical', color: '#dc2626', bg: 'rgba(220,38,38,0.12)' },
];

// How widely the contract may be shared. Informational for now — a later phase
// derives access rules from it.
const CONFIDENTIALITY_OPTIONS = [
  { value: 'public', labelKey: 'contractForm.confidentialityLevels.public', color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
  { value: 'internal', labelKey: 'contractForm.confidentialityLevels.internal', color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' },
  { value: 'confidential', labelKey: 'contractForm.confidentialityLevels.confidential', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
  { value: 'restricted', labelKey: 'contractForm.confidentialityLevels.restricted', color: '#dc2626', bg: 'rgba(220,38,38,0.12)' },
];

// ─── Section Header ──────────────────────────────────────────────────────────
// `action` renders an interactive control in the header's top-right corner. It
// sits beside the toggle button rather than inside it, so the control keeps its
// own click target instead of collapsing the section.
function SectionHeader({ sectionKey, title, subtitle, icon: Icon, expanded, onToggle, badge, color, completed, action }) {
  const header = (
    <button
      type="button"
      className={`cf-section__header ${expanded ? 'cf-section__header--expanded' : ''}`}
      onClick={() => onToggle(sectionKey)}
    >
      <div className="cf-section__header-left">
        {Icon && (
          <div className="cf-section__header-icon" style={color ? { background: color + '18', color } : {}}>
            <Icon size={16} />
          </div>
        )}
        <div className="cf-section__header-text">
          <span className="cf-section__header-title">{title}</span>
          {subtitle && <span className="cf-section__header-subtitle">{subtitle}</span>}
        </div>
      </div>
      <div className="cf-section__header-right">
        {completed && <FiCheckCircle size={14} className="cf-section__check" />}
        {badge && <span className="cf-section__badge">{badge}</span>}
        <FiChevronDown className={`cf-section__chevron ${expanded ? 'cf-section__chevron--open' : ''}`} />
      </div>
    </button>
  );

  if (!action) return header;

  return (
    <div className="cf-section__headrow">
      {header}
      <div className="cf-section__headaction">{action}</div>
    </div>
  );
}

// ─── Checkbox ────────────────────────────────────────────────────────────────
function CheckboxField({ checked, onChange, label }) {
  return (
    <label className={`cf-check${checked ? ' cf-check--on' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="cf-check__box" aria-hidden="true">
        {checked && <FiCheck size={12} />}
      </span>
      <span className="cf-check__label">{label}</span>
    </label>
  );
}

// ─── Toggle Switch ───────────────────────────────────────────────────────────
function ToggleSwitch({ checked, onChange, labelOn, labelOff }) {
  return (
    <button
      type="button"
      className={`cf-toggle ${checked ? 'cf-toggle--on' : ''}`}
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
    >
      <span className="cf-toggle__track">
        <span className="cf-toggle__thumb" />
      </span>
      <span className="cf-toggle__label">{checked ? labelOn : labelOff}</span>
    </button>
  );
}

// ─── Chip Selector ───────────────────────────────────────────────────────────
// Single-choice pill row, shared by Priority and Confidentiality Level.
function ChipSelector({ options, value, onChange, t }) {
  return (
    <div className="cf-priority">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          className={`cf-priority__item ${value === opt.value ? 'cf-priority__item--active' : ''}`}
          onClick={() => onChange(opt.value)}
          style={value === opt.value ? { borderColor: opt.color, background: opt.bg, color: opt.color } : {}}
        >
          <span className="cf-priority__dot" style={{ background: opt.color }} />
          {t(opt.labelKey)}
        </button>
      ))}
    </div>
  );
}

// ─── Tag Input ───────────────────────────────────────────────────────────────
function TagInput({ value, onChange }) {
  const [input, setInput] = useState('');
  const tags = value ? value.split(',').map(t => t.trim()).filter(Boolean) : [];

  const addTag = () => {
    const trimmed = input.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed].join(', '));
      setInput('');
    }
  };

  const removeTag = (idx) => {
    onChange(tags.filter((_, i) => i !== idx).join(', '));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
    if (e.key === 'Backspace' && !input && tags.length) {
      removeTag(tags.length - 1);
    }
  };

  return (
    <div className="cf-tags">
      <div className="cf-tags__list">
        {tags.map((tag, i) => (
          <span key={i} className="cf-tags__chip">
            {tag}
            <button type="button" onClick={() => removeTag(i)} className="cf-tags__chip-remove">
              <FiX size={12} />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={addTag}
          placeholder={tags.length ? '' : 'Type and press Enter to add tags...'}
          className="cf-tags__input"
        />
      </div>
    </div>
  );
}

// ─── Obligation options ──────────────────────────────────────────────────────
const OBLIGATION_TYPE_OPTIONS = [
  { value: 'payment', labelKey: 'contractForm.obligations.type.payment' },
  { value: 'delivery', labelKey: 'contractForm.obligations.type.delivery' },
  { value: 'reporting', labelKey: 'contractForm.obligations.type.reporting' },
  { value: 'compliance', labelKey: 'contractForm.obligations.type.compliance' },
  { value: 'review', labelKey: 'contractForm.obligations.type.review' },
  { value: 'other', labelKey: 'contractForm.obligations.type.other' },
];
const OBLIGATION_STATUS_OPTIONS = [
  { value: 'pending', labelKey: 'contractForm.obligations.status.pending' },
  { value: 'in_progress', labelKey: 'contractForm.obligations.status.in_progress' },
  { value: 'completed', labelKey: 'contractForm.obligations.status.completed' },
  { value: 'overdue', labelKey: 'contractForm.obligations.status.overdue' },
  { value: 'cancelled', labelKey: 'contractForm.obligations.status.cancelled' },
];

const EMPTY_OBLIGATION = { title: '', description: '', type: 'other', dueDate: '', owner: '', status: 'pending' };

// Statuses in which signing is under way, so a signed copy can exist. Mirrors
// the guard on POST /contracts/:id/documents for category 'signed'.
const SIGNATURE_STAGE_STATUSES = ['sent_for_signature', 'partially_signed', 'fully_executed'];

// ─── Obligation editor (used in create and edit) ─────────────────────────────
// Obligations are persisted as their own records (Obligation collection) linked
// to the contract — see the create/update handlers in ContractForm.
function ObligationEditor({ obligations, onChange, users, t }) {
  const addObligation = () => onChange([...obligations, { ...EMPTY_OBLIGATION }]);
  const removeObligation = (idx) => onChange(obligations.filter((_, i) => i !== idx));
  const updateObligation = (idx, field, value) => {
    const next = obligations.map((o, i) => (i === idx ? { ...o, [field]: value } : o));
    onChange(next);
  };

  const ownerOptions = [
    { value: '', label: t('contractForm.obligations.noOwner', { defaultValue: 'Unassigned' }) },
    ...users.map((u) => ({ value: u._id, label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email, sublabel: u.email })),
  ];

  return (
    <div className="cf-obls">
      {obligations.length === 0 ? (
        <div className="cf-obls__empty">
          <FiCheckCircle size={20} />
          <span>{t('contractForm.obligations.emptyHint', { defaultValue: 'No obligations added yet. Add deliverables, payments or deadlines to track.' })}</span>
        </div>
      ) : (
        <div className="cf-obls__list">
          {obligations.map((ob, idx) => (
            <div key={ob._id || idx} className="cf-obl">
              <div className="cf-obl__head">
                <span className="cf-obl__num">{idx + 1}</span>
                <input
                  className="cf-obl__title"
                  type="text"
                  value={ob.title}
                  onChange={(e) => updateObligation(idx, 'title', e.target.value)}
                  placeholder={t('contractForm.obligations.titlePlaceholder', { defaultValue: 'Obligation title (e.g. First milestone payment)' })}
                  maxLength={300}
                />
                <button type="button" className="cf-obl__remove" onClick={() => removeObligation(idx)} aria-label={t('common.delete', { defaultValue: 'Delete' })}>
                  <FiTrash2 size={14} />
                </button>
              </div>
              <div className="cf-obl__grid">
                <div className="cf-obl__field">
                  <span>{t('contractForm.obligations.typeLabel', { defaultValue: 'Type' })}</span>
                  <SearchableSelect
                    value={ob.type}
                    onChange={(v) => updateObligation(idx, 'type', v)}
                    options={OBLIGATION_TYPE_OPTIONS.map(o => ({ value: o.value, label: t(o.labelKey, { defaultValue: o.value }) }))}
                  />
                </div>
                <div className="cf-obl__field">
                  <span>{t('contractForm.obligations.dueDateLabel', { defaultValue: 'Due date' })} <span className="cf-field__required">*</span></span>
                  <DateField
                    value={ob.dueDate || ''}
                    onChange={(v) => updateObligation(idx, 'dueDate', v)}
                  />
                </div>
                <div className="cf-obl__field">
                  <span>{t('contractForm.obligations.statusLabel', { defaultValue: 'Status' })}</span>
                  <SearchableSelect
                    value={ob.status}
                    onChange={(v) => updateObligation(idx, 'status', v)}
                    options={OBLIGATION_STATUS_OPTIONS.map(o => ({ value: o.value, label: t(o.labelKey, { defaultValue: o.value }) }))}
                  />
                </div>
                <div className="cf-obl__field">
                  <span>{t('contractForm.obligations.ownerLabel', { defaultValue: 'Owner' })}</span>
                  <SearchableSelect
                    value={ob.owner || ''}
                    onChange={(v) => updateObligation(idx, 'owner', v)}
                    options={ownerOptions}
                    searchPlaceholder={t('contractForm.searchUser', { defaultValue: 'Search…' })}
                  />
                </div>
              </div>
              <label className="cf-obl__field cf-obl__field--full">
                <span>{t('contractForm.obligations.descriptionLabel', { defaultValue: 'Description' })}</span>
                <textarea
                  rows={2}
                  value={ob.description}
                  onChange={(e) => updateObligation(idx, 'description', e.target.value)}
                  placeholder={t('contractForm.obligations.descriptionPlaceholder', { defaultValue: 'Describe the obligation…' })}
                  maxLength={2000}
                />
              </label>
            </div>
          ))}
        </div>
      )}
      <button type="button" className="cf-obls__add" onClick={addObligation}>
        <FiPlus size={14} />
        <span>{t('contractForm.obligations.addObligation', { defaultValue: 'Add obligation' })}</span>
      </button>
    </div>
  );
}

// A stored radio value may be the option id (current form), the localized
// label (values saved before ids were used), or a boolean (fields converted
// from checkbox — their options carry the yes/no ids).
function radioValueMatches(value, opt) {
  return value === opt.id || value === opt.label
    || (value === true && opt.id === 'yes')
    || (value === false && opt.id === 'no');
}

// A yes/no radio gates the rest of its section — answer "no" and the fields
// below usually collapse away. Those render in the section header instead of
// the body, which keeps the question in view and saves a row of card height.
function isYesNoField(field) {
  if (field?.type !== 'radio' || field.isActive === false) return false;
  const ids = (field.options || []).map((o) => o.id);
  return ids.length === 2 && ids.includes('yes') && ids.includes('no');
}

// ─── Compute hidden fields based on radio selections ─────────────────────────
function computeHiddenFields(fields, fieldData, sectionId) {
  const hiddenIds = new Set();
  const visibleIds = new Set();

  fields.forEach((field) => {
    if (field.type !== 'radio') return;
    const key = `${sectionId}_${field.id}`;
    const selectedValue = fieldData[key];

    if (!selectedValue) {
      const defRule = field.defaultVisibilityRule;
      if (defRule && defRule.fieldIds && defRule.fieldIds.length > 0) {
        if (defRule.action === 'show') {
          defRule.fieldIds.forEach((id) => visibleIds.add(id));
        } else if (defRule.action === 'hide') {
          defRule.fieldIds.forEach((id) => hiddenIds.add(id));
        }
      }
      return;
    }

    (field.options || []).forEach((opt) => {
      if (!radioValueMatches(selectedValue, opt)) return;
      const rule = opt.visibilityRule;
      if (!rule || !rule.fieldIds || rule.fieldIds.length === 0) return;
      if (rule.action === 'show') {
        rule.fieldIds.forEach((id) => visibleIds.add(id));
      } else if (rule.action === 'hide') {
        rule.fieldIds.forEach((id) => hiddenIds.add(id));
      }
    });
  });

  const hasShowRules = fields.some((f) => {
    if (f.type !== 'radio') return false;
    const key = `${sectionId}_${f.id}`;
    const selectedValue = fieldData[key];
    if (!selectedValue) {
      const defRule = f.defaultVisibilityRule;
      return defRule && defRule.action === 'show' && (defRule.fieldIds || []).length > 0;
    }
    return (f.options || []).some(
      (o) => radioValueMatches(selectedValue, o) && o.visibilityRule && o.visibilityRule.action === 'show' && (o.visibilityRule.fieldIds || []).length > 0
    );
  });

  if (hasShowRules) {
    fields.forEach((f) => {
      if (f.type === 'radio') return;
      if (!visibleIds.has(f.id)) {
        hiddenIds.add(f.id);
      }
    });
  }

  return hiddenIds;
}

// ─── On/off switch for schema checkbox fields ────────────────────────────────
// Values are stored as 'true'/'false' strings, same as the old native checkbox.
function SchemaSwitch({ value, onToggle, stateLabel = true }) {
  const { t } = useTranslation();
  const on = value === 'true' || value === true;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      className={`cf-switch ${on ? 'is-on' : ''}`}
      onClick={() => onToggle(on ? 'false' : 'true')}
    >
      <span className="cf-switch__track"><span className="cf-switch__thumb" /></span>
      {stateLabel && (
        <span className="cf-switch__state">{on ? t('common.yes') : t('common.no')}</span>
      )}
    </button>
  );
}

// ─── Schema-driven field renderer ────────────────────────────────────────────
function SchemaField({ field, value, onChange }) {
  const handleChange = (val) => onChange(field.id, val);

  switch (field.type) {
    case 'radio':
      // Selections are stored by option id, so the saved value survives
      // language switches and label edits (legacy label values still match).
      return (
        <div className="cf-radio-group">
          {(field.options || []).map((opt) => (
            <label
              key={opt.id}
              className={`cf-radio ${radioValueMatches(value, opt) ? 'cf-radio--active' : ''}`}
            >
              <input
                type="radio"
                name={`radio_${field.id}`}
                value={opt.id}
                checked={radioValueMatches(value, opt)}
                onChange={() => handleChange(opt.id)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      );
    case 'text':
      return (
        <input
          type="text"
          value={value || ''}
          onChange={e => handleChange(e.target.value)}
          placeholder={field.placeholder}
          required={field.required}
        />
      );
    case 'number':
      return (
        <input
          type="number"
          value={value || ''}
          onChange={e => handleChange(e.target.value)}
          placeholder={field.placeholder}
          required={field.required}
        />
      );
    case 'currency':
      // No hardcoded currency symbol: schema amounts carry their own currency
      // select alongside (see the standard financial template), so a fixed "$"
      // would mislead. Entry and display follow the user's region format
      // (thousands separators + two decimals); the stored value stays a plain
      // dot-decimal string.
      return (
        <MoneyInput
          value={value || ''}
          onChange={v => handleChange(v)}
          required={field.required}
        />
      );
    case 'date':
      return (
        <DateField
          value={value || ''}
          onChange={(v) => handleChange(v)}
          required={field.required}
        />
      );
    case 'textarea':
      return (
        <textarea
          value={value || ''}
          onChange={e => handleChange(e.target.value)}
          placeholder={field.placeholder}
          rows={3}
          required={field.required}
        />
      );
    case 'checkbox':
      // The wrapper already prints the field label above — the control is a
      // bare switch with its on/off state spelled out next to it.
      return <SchemaSwitch value={value} onToggle={handleChange} />;
    case 'email':
      return (
        <input
          type="email"
          value={value || ''}
          onChange={e => handleChange(e.target.value)}
          placeholder={field.placeholder || 'email@example.com'}
          required={field.required}
        />
      );
    case 'phone':
      return (
        <input
          type="tel"
          value={value || ''}
          onChange={e => handleChange(e.target.value)}
          placeholder={field.placeholder || '+1 (555) 000-0000'}
          required={field.required}
        />
      );
    case 'select':
      return (
        <SearchableSelect
          value={value || ''}
          onChange={(v) => handleChange(v)}
          required={field.required}
          clearable={!field.required}
          options={(field.options || []).map(opt => ({ value: opt.label, label: opt.label }))}
        />
      );
    case 'file':
      return (
        <input
          type="file"
          onChange={e => handleChange(e.target.files[0]?.name || '')}
          required={field.required}
        />
      );
    default:
      return (
        <input
          type="text"
          value={value || ''}
          onChange={e => handleChange(e.target.value)}
          placeholder={field.placeholder}
          required={field.required}
        />
      );
  }
}

// ─── Schema-driven table section ─────────────────────────────────────────────
function SchemaTableSection({ section, tableRows, onTableChange }) {
  const addRow = () => onTableChange(section.id, [...tableRows, {}]);
  const removeRow = (idx) => onTableChange(section.id, tableRows.filter((_, i) => i !== idx));
  const updateCell = (rowIdx, colId, val) => {
    const rows = [...tableRows];
    rows[rowIdx] = { ...rows[rowIdx], [colId]: val };
    onTableChange(section.id, rows);
  };

  return (
    <div className="cf-table-wrap">
      <table className="cf-table">
        <thead>
          <tr>
            <th className="cf-table__row-num">#</th>
            {(section.tableColumns || []).map(col => (
              <th key={col.id}>{col.header}{col.required && ' *'}</th>
            ))}
            <th className="cf-table__actions-col"></th>
          </tr>
        </thead>
        <tbody>
          {tableRows.length === 0 && (
            <tr className="cf-table__empty-row">
              <td colSpan={(section.tableColumns || []).length + 2}>
                <div className="cf-table__empty">
                  <FiGrid size={20} />
                  <span>No rows yet. Click below to add one.</span>
                </div>
              </td>
            </tr>
          )}
          {tableRows.map((row, rowIdx) => (
            <tr key={rowIdx} className="cf-table__row">
              <td className="cf-table__row-num">{rowIdx + 1}</td>
              {(section.tableColumns || []).map(col => (
                <td key={col.id}>
                  {col.type === 'select' ? (
                    <select
                      value={row[col.id] || ''}
                      onChange={e => updateCell(rowIdx, col.id, e.target.value)}
                    >
                      <option value="">Select...</option>
                      {(col.options || []).map(o => (
                        <option key={o.id} value={o.label}>{o.label}</option>
                      ))}
                    </select>
                  ) : col.type === 'date' ? (
                    <input
                      type="date"
                      value={row[col.id] || ''}
                      onChange={e => updateCell(rowIdx, col.id, e.target.value)}
                    />
                  ) : col.type === 'checkbox' ? (
                    <SchemaSwitch
                      value={row[col.id]}
                      onToggle={(v) => updateCell(rowIdx, col.id, v)}
                      stateLabel={false}
                    />
                  ) : col.type === 'currency' ? (
                    <MoneyInput
                      value={row[col.id] || ''}
                      onChange={v => updateCell(rowIdx, col.id, v)}
                    />
                  ) : (
                    <input
                      type={col.type === 'number' ? 'number' : 'text'}
                      value={row[col.id] || ''}
                      onChange={e => updateCell(rowIdx, col.id, e.target.value)}
                      placeholder="..."
                    />
                  )}
                </td>
              ))}
              <td className="cf-table__actions-col">
                <button type="button" className="cf-table__remove-btn" onClick={() => removeRow(rowIdx)}>
                  <FiTrash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" className="cf-table__add-btn" onClick={addRow}>
        <FiPlus size={14} />
        <span>Add row</span>
      </button>
    </div>
  );
}

// ─── Main ContractForm component ─────────────────────────────────────────────
function hexToRgba(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function ContractForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef(null);
  const signedInputRef = useRef(null);
  const attachInputRef = useRef(null);
  const deptRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [signedDragOver, setSignedDragOver] = useState(false);
  const [attachDragOver, setAttachDragOver] = useState(false);

  const liveAccent = useSelector(
    (state) => state.settings?.branding?.accentColor
      || state.settings?.config?.branding?.accentColor
      || '#6366f1'
  );
  // The logged-in user — default responsible person on new contracts.
  const authUser = useSelector((state) => state.auth?.user || null);

  const [core, setCore] = useState({ ...INITIAL_CORE });
  // Per-field validation messages ({ coreFieldKey: message }), set when a send
  // action fails validation and cleared as the user fixes each field.
  const [fieldErrors, setFieldErrors] = useState({});
  const [fieldData, setFieldData] = useState({});
  const [obligations, setObligations] = useState([]);
  const [originalObligationIds, setOriginalObligationIds] = useState([]);
  const [users, setUsers] = useState([]);
  // Populated responsible user from an edited contract — keeps the picker able
  // to label the current value even when the users list is unavailable
  // (usersAPI.list is MANAGE_USERS-gated and may 403 for regular users).
  const [editResponsibleUser, setEditResponsibleUser] = useState(null);
  // The draft contract document → becomes an editable (versioned) document.
  const [contractFile, setContractFile] = useState(null);
  // The signed contract — only uploadable once the contract reaches signing.
  const [signedFile, setSignedFile] = useState(null);
  // Supporting files → stored as plain attachments on the contract.
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  // Documents already filed against the contract being edited. Uploading into
  // an occupied slot versions that document instead of creating a second one,
  // so the slot says what is in it rather than looking empty.
  const [existingDocs, setExistingDocs] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    coreIdentity: true,
    counterparty: true,
    dates: true,
  });
  const [cpLookupOpen, setCpLookupOpen] = useState(false);
  const [cpLookupItems, setCpLookupItems] = useState([]);
  const [cpLookupLoading, setCpLookupLoading] = useState(false);
  const [cpLookupGlobalSearch, setCpLookupGlobalSearch] = useState('');
  const [cpLookupColumnFilters, setCpLookupColumnFilters] = useState({});
  const [cpActiveIndex, setCpActiveIndex] = useState(0);
  const cpListRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  // Which action is running, so the right button shows a spinner:
  // 'draft' | 'review' | 'approval' | 'update'.
  const [submitAction, setSubmitAction] = useState(null);
  // Status of the contract being edited (drives which action buttons show).
  const [contractStatus, setContractStatus] = useState('');
  // Version info of the contract being edited: amendment/addendum versions
  // inherit Contract Type and Counterparty from the original — both stay
  // read-only in the form (enforced server-side as well).
  const [versionMeta, setVersionMeta] = useState(null); // { versionType, majorVersion, versionChangeNote }
  const isVersionContract = ['amendment', 'addendum'].includes(versionMeta?.versionType);
  // The signed copy only exists once signing is under way, so its upload slot
  // stays disabled until the contract reaches the signature stage — unless the
  // contract is being recorded as already signed, which unlocks it at step one.
  const signedUploadEnabled = core.isSignedContract || SIGNATURE_STAGE_STATUSES.includes(contractStatus);
  // Recording a signed contract means the signed file is not optional.
  const signedUploadRequired = !!core.isSignedContract;
  const signedUploadMissing = signedUploadRequired && !signedFile;
  // Documents are attached while the contract is still being drafted. Once it
  // has moved on, they are managed from the contract's own Documents tab, so
  // the section drops out of the edit form entirely.
  const showDocumentsSection = !isEdit || contractStatus === 'draft';
  const existingDraftDoc = existingDocs.find(d => d.category === 'draft') || null;
  const existingSignedDoc = existingDocs.find(d => d.category === 'signed') || null;
  // Signed = No → Draft = Required: a contract that is not recorded as
  // already signed cannot move past creation without its draft document.
  // Signed = Yes → the draft is optional. Saving as draft always works.
  const draftUploadRequired = !core.isSignedContract;
  const draftUploadMissing = draftUploadRequired && !contractFile && !existingDraftDoc;
  // Template Library picker — browse/download the standard templates defined
  // for the selected Company + Contract Type without leaving the form.
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const canBrowseTemplates = TEMPLATE_LIBRARY_ENABLED && hasAnyPermission(
    authUser?.role,
    ['view_template_library', 'manage_template_library'],
    authUser?.effectivePermissions || null
  );
  // Workflow picker (shown when sending for review/approval).
  const [workflows, setWorkflows] = useState([]);
  const [wfPickerOpen, setWfPickerOpen] = useState(false);
  const [wfSearch, setWfSearch] = useState('');
  const [contractTypes, setContractTypes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [supportingDocTypesList, setSupportingDocTypesList] = useState([]);
  const [counterpartyTypesList, setCounterpartyTypesList] = useState([]);
  const [companiesList, setCompaniesList] = useState([]);
  // A contract may reference a counterparty whose type was deactivated later.
  // The active-only list won't contain it, so fetch that one type on demand and
  // show it (flagged) instead of rendering an empty selector.
  const [inactiveCpType, setInactiveCpType] = useState(null);
  const [selectedCpTypeId, setSelectedCpTypeId] = useState('');
  const [departments, setDepartments] = useState([]);
  const [deptSearch, setDeptSearch] = useState('');
  const [deptDropdownOpen, setDeptDropdownOpen] = useState(false);
  const [fieldSchema, setFieldSchema] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(false);

  // Compute progress
  const progress = useMemo(() => {
    let filled = 0;
    let total = 5; // required: contractDate, contractTypeId, counterpartyName + optional tracked fields
    if (core.contractDate) filled++;
    if (core.contractTypeId) filled++;
    if (core.counterpartyName) filled++;
    if (core.startDate) filled++;
    if (core.endDate) filled++;
    if (core.contractAmount) { filled++; total++; }
    else total++;
    if (core.signingDate) { filled++; total++; }
    else total++;
    return Math.round((filled / total) * 100);
  }, [core]);

  useEffect(() => {
    companiesAPI.list({ active: 'true' })
      .then(r => setCompaniesList(r.data.companies || []))
      .catch(() => {});
  }, []);

  // Definitions, org structure, and people are all company-scoped: the picker
  // lists reload whenever the contract's company changes, narrowed to
  // "All Companies" definitions plus the selected company's own.
  // The company starts '' and is defaulted a beat later, so two fetches race
  // on first load — the token makes sure only the LATEST company's responses
  // land (an unscoped response must not overwrite the scoped list).
  const pickerLoadToken = useRef(0);
  useEffect(() => {
    const token = ++pickerLoadToken.current;
    const fresh = () => token === pickerLoadToken.current;
    const scope = core.companyId ? { companyId: core.companyId } : {};
    parametersAPI.contractTypes.list({ active: 'true', ...scope })
      .then(r => { if (fresh()) setContractTypes(r.data.items || []); })
      .catch(() => {});
    parametersAPI.currencies.list({ active: 'true', ...scope })
      .then(r => { if (fresh()) setCurrencies(r.data.items || []); })
      .catch(() => {});
    parametersAPI.counterpartyTypes.list({ active: 'true', ...scope })
      .then(r => { if (fresh()) setCounterpartyTypesList(r.data.items || []); })
      .catch(() => {});
    parametersAPI.supportingDocTypes.list({ active: 'true', ...scope })
      .then(r => { if (fresh()) setSupportingDocTypesList(r.data.items || []); })
      .catch(() => {});
    departmentsAPI.list(scope)
      .then(r => { if (fresh()) setDepartments((r.data.departments || []).filter(d => d.isActive)); })
      .catch(() => {});
    usersAPI.list({ limit: 500, ...scope })
      .then(r => { if (fresh()) setUsers(r.data.users || []); })
      .catch(() => {});
  }, [core.companyId]);

  // Close department dropdown on click outside
  useEffect(() => {
    const handler = (e) => {
      if (deptRef.current && !deptRef.current.contains(e.target)) setDeptDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredDepts = departments.filter(d =>
    d.name.toLowerCase().includes(deptSearch.toLowerCase())
  );

  // ── Responsible person / department defaults ──
  // Resolve a user's department to the NAME the contract stores. The users
  // list carries departmentRef populated; the auth user only carries a bare
  // id, so fall back to matching the loaded departments (then the legacy
  // free-text `department` field).
  const deptNameOfUser = useCallback((u) => {
    if (!u) return '';
    const ref = u.departmentRef;
    if (ref && typeof ref === 'object' && ref.name) return ref.name;
    if (ref) {
      const d = departments.find((x) => String(x._id) === String(ref));
      if (d) return d.name;
    }
    return u.department || '';
  }, [departments]);

  // New contracts default the company to the creator's own — the mandatory
  // Company field starts filled and every dependent picker scopes to it.
  const companyDefaultRef = useRef(false);
  useEffect(() => {
    if (isEdit || companyDefaultRef.current || !authUser) return;
    const cid = authUser.companyId?._id || authUser.companyId || '';
    if (!cid) return;
    companyDefaultRef.current = true;
    setCore((prev) => (prev.companyId ? prev : { ...prev, companyId: cid }));
  }, [isEdit, authUser]);

  // Switching company invalidates every company-scoped selection: contract
  // type (and its schema/workflow), currency, counterparty, and the
  // responsible department are cleared so nothing from the old company's
  // scope survives on the contract.
  const handleCompanyChange = (v) => {
    setCore(p => ({
      ...p,
      companyId: v,
      contractTypeId: '',
      currency: '',
      counterpartyId: '',
      counterpartyName: '',
      responsibleDepartmentId: '',
    }));
    setSelectedCpTypeId('');
    setFieldSchema(null);
    clearFieldError('companyId');
  };

  // New contracts default the responsible person to the creator…
  const personDefaultRef = useRef(false);
  useEffect(() => {
    if (isEdit || personDefaultRef.current || !authUser?._id) return;
    personDefaultRef.current = true;
    setCore((prev) => (prev.responsibleUserId ? prev : { ...prev, responsibleUserId: authUser._id }));
  }, [isEdit, authUser]);

  // …and the responsible department to the creator's department. Waits for the
  // departments list when the auth user carries only a bare department id; a
  // user without a department leaves the field empty for manual selection.
  const deptDefaultRef = useRef(false);
  useEffect(() => {
    if (isEdit || deptDefaultRef.current || !authUser) return;
    const name = deptNameOfUser(authUser);
    const hasBareRef = authUser.departmentRef && typeof authUser.departmentRef !== 'object';
    if (!name && hasBareRef && departments.length === 0) return;
    deptDefaultRef.current = true;
    if (!name) return;
    setCore((prev) => (prev.responsibleDepartmentId ? prev : { ...prev, responsibleDepartmentId: name }));
  }, [isEdit, authUser, departments, deptNameOfUser]);

  // Picker options: the tenant's users plus whoever must be labelable even
  // when the list request is denied (the creator, the saved responsible).
  const responsibleUserOptions = useMemo(() => {
    const map = new Map();
    const add = (u) => {
      if (!u?._id || map.has(String(u._id))) return;
      map.set(String(u._id), {
        value: u._id,
        label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '',
        sublabel: u.email || '',
      });
    };
    users.forEach(add);
    add(authUser);
    add(editResponsibleUser);
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [users, authUser, editResponsibleUser]);

  // Changing the responsible person re-syncs the department to theirs; when
  // their department is unknown the field clears for manual selection.
  const handleResponsibleChange = useCallback((userId) => {
    setCore((prev) => {
      const next = { ...prev, responsibleUserId: userId || '' };
      if (userId) {
        const u = users.find((x) => String(x._id) === String(userId))
          || (authUser && String(authUser._id) === String(userId) ? authUser : null)
          || (editResponsibleUser && String(editResponsibleUser._id) === String(userId) ? editResponsibleUser : null);
        next.responsibleDepartmentId = deptNameOfUser(u);
      }
      return next;
    });
  }, [users, authUser, editResponsibleUser, deptNameOfUser]);

  // Open counterparty lookup modal — load all counterparties for selected type
  const openCpLookup = () => {
    if (!selectedCpTypeId) {
      toast.error(t('contractForm.selectCpTypeFirst'));
      return;
    }
    setCpLookupOpen(true);
    setCpLookupGlobalSearch('');
    setCpLookupColumnFilters({});
    setCpLookupLoading(true);
    parametersAPI.counterparties.list({ typeId: selectedCpTypeId, active: 'true', limit: 500 })
      .then(r => setCpLookupItems(r.data.items || []))
      .catch(() => setCpLookupItems([]))
      .finally(() => setCpLookupLoading(false));
  };

  // Filter lookup items by global search + column filters
  const filteredCpLookup = useMemo(() => {
    let items = cpLookupItems;
    // Global search
    if (cpLookupGlobalSearch.trim()) {
      const q = cpLookupGlobalSearch.toLowerCase();
      items = items.filter(cp =>
        (cp.name || '').toLowerCase().includes(q) ||
        (cp.code || '').toLowerCase().includes(q) ||
        (cp.country || '').toLowerCase().includes(q) ||
        (cp.city || '').toLowerCase().includes(q) ||
        (cp.phone || '').toLowerCase().includes(q) ||
        (cp.email || '').toLowerCase().includes(q) ||
        (cp.contactPerson || '').toLowerCase().includes(q) ||
        (cp.address || '').toLowerCase().includes(q)
      );
    }
    // Per-column filters
    Object.entries(cpLookupColumnFilters).forEach(([col, val]) => {
      if (val && val.trim()) {
        const q = val.toLowerCase();
        items = items.filter(cp => (cp[col] || '').toLowerCase().includes(q));
      }
    });
    return items;
  }, [cpLookupItems, cpLookupGlobalSearch, cpLookupColumnFilters]);

  // Reset the keyboard-highlight to the top whenever the result set changes.
  useEffect(() => { setCpActiveIndex(0); }, [cpLookupGlobalSearch, cpLookupOpen]);

  // Keep the highlighted row scrolled into view.
  useEffect(() => {
    if (!cpLookupOpen) return;
    const el = cpListRef.current?.querySelector('[data-cp-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [cpActiveIndex, cpLookupOpen]);

  // Arrow-key navigation + Enter to select + Esc to close, like a command palette.
  useEffect(() => {
    if (!cpLookupOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { setCpLookupOpen(false); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setCpActiveIndex(i => Math.min(i + 1, filteredCpLookup.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setCpActiveIndex(i => Math.max(i - 1, 0)); }
      else if (e.key === 'Enter') {
        const cp = filteredCpLookup[cpActiveIndex];
        if (cp) { e.preventDefault(); handleSelectCounterparty(cp); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cpLookupOpen, filteredCpLookup, cpActiveIndex]);

  const handleSelectCounterparty = (cp) => {
    setCore(prev => ({
      ...prev,
      counterpartyId: cp._id,
      counterpartyName: cp.name,
      counterpartyAddress: cp.address || '',
      counterpartyCity: cp.city || '',
      counterpartyCountry: cp.country || '',
      counterpartyPhone: cp.phone || '',
      counterpartyEmail: cp.email || '',
      counterpartyContactPerson: cp.contactPerson || '',
    }));
    setCpLookupOpen(false);
  };

  const handleClearCounterparty = () => {
    setCore(prev => ({
      ...prev,
      counterpartyId: '',
      counterpartyName: '',
      counterpartyAddress: '',
      counterpartyCity: '',
      counterpartyCountry: '',
      counterpartyPhone: '',
      counterpartyEmail: '',
      counterpartyContactPerson: '',
    }));
  };

  // Keep the raw schema (labels may be multilang maps) and resolve it to the
  // active language separately, so switching language re-labels the form
  // without another round-trip.
  const [rawFieldSchema, setRawFieldSchema] = useState(null);
  // Workflow template assigned to the selected contract type. When set, send
  // for review/approval uses it directly instead of opening the picker.
  const [typeWorkflow, setTypeWorkflow] = useState(null);

  const loadSchema = useCallback(async (typeId) => {
    if (!typeId) { setRawFieldSchema(null); setTypeWorkflow(null); return; }
    setSchemaLoading(true);
    try {
      const res = await parametersAPI.contractTypes.getConfig(typeId);
      setRawFieldSchema(res.data.fieldSchema || null);
      setTypeWorkflow(res.data.workflow || null);
    } catch {
      setRawFieldSchema(null);
      setTypeWorkflow(null);
    }
    setSchemaLoading(false);
  }, []);

  useEffect(() => {
    if (core.contractTypeId) loadSchema(core.contractTypeId);
  }, [core.contractTypeId, loadSchema]);

  useEffect(() => {
    setFieldSchema(rawFieldSchema ? sanitizeFieldSchema(rawFieldSchema, i18n.language) : null);
  }, [rawFieldSchema, i18n.language]);

  useEffect(() => {
    if (!selectedCpTypeId) { setInactiveCpType(null); return; }
    const inActive = counterpartyTypesList.some(
      (ct) => String(ct._id) === String(selectedCpTypeId),
    );
    if (inActive) { setInactiveCpType(null); return; }
    if (String(inactiveCpType?._id) === String(selectedCpTypeId)) return;

    let cancelled = false;
    parametersAPI.counterpartyTypes.get(selectedCpTypeId)
      .then((r) => { if (!cancelled) setInactiveCpType(r.data.item || null); })
      .catch(() => { if (!cancelled) setInactiveCpType(null); });
    return () => { cancelled = true; };
  }, [selectedCpTypeId, counterpartyTypesList, inactiveCpType]);

  // Map an obligation record (from the API) to the editor's row shape.
  const toObligationRow = (o) => ({
    _id: o._id,
    title: o.title || '',
    description: o.description || '',
    type: o.type || 'other',
    dueDate: o.dueDate ? o.dueDate.split('T')[0] : '',
    owner: o.owner?._id || o.owner || '',
    status: o.status || 'pending',
  });

  useEffect(() => {
    if (isEdit) {
      contractsAPI.get(id).then(({ data }) => {
        const c = data.contract;
        setContractStatus(c.status || '');
        setVersionMeta({
          versionType: c.versionType || 'original',
          // V0 = original, V1+ = versions (?? — 0 is a valid number).
          majorVersion: c.versionType && c.versionType !== 'original' ? (c.majorVersion ?? 1) : 0,
          versionChangeNote: c.versionChangeNote || '',
        });
        const obs = Array.isArray(data.obligations) ? data.obligations : [];
        setObligations(obs.map(toObligationRow));
        setOriginalObligationIds(obs.map((o) => o._id).filter(Boolean));
        setCore({
          contractName: c.contractName || '',
          contractDate: c.contractDate ? c.contractDate.split('T')[0] : new Date().toISOString().split('T')[0],
          contractTypeId: c.contractTypeId?._id || c.contractTypeId || '',
          responsibleUserId: c.responsibleUserId?._id || c.responsibleUserId || '',
          responsibleDepartmentId: c.responsibleDepartmentId || '',
          companyId: c.companyId?._id || c.companyId || '',
          counterpartyName: c.counterpartyName || '',
          counterpartyId: c.counterpartyId?._id || c.counterpartyId || '',
          counterpartyAddress: c.counterpartyAddress || '',
          counterpartyCity: c.counterpartyCity || '',
          counterpartyCountry: c.counterpartyCountry || '',
          counterpartyPhone: c.counterpartyPhone || '',
          counterpartyEmail: c.counterpartyEmail || '',
          counterpartyContactPerson: c.counterpartyContactPerson || '',
          startDate: c.startDate ? c.startDate.split('T')[0] : '',
          endDate: c.endDate ? c.endDate.split('T')[0] : '',
          signingDate: c.signingDate ? c.signingDate.split('T')[0] : '',
          reminderDays: c.reminderDays ?? '',
          autoRenewal: c.autoRenewal || false,
          contractAmount: c.contractAmount ?? '',
          currency: c.currency || '',
          financialNote: c.financialNote || '',
          priority: c.priority || 'medium',
          confidentialityLevel: c.confidentialityLevel || 'internal',
          templateSource: c.templateSource || '',
          isSignedContract: !!c.isSignedContract,
          tags: (c.tags || []).join(', '),
        });
        setFieldData(c.fieldData || {});
        // Keep the populated responsible user so the picker can label the
        // saved value even when the users list request is denied.
        setEditResponsibleUser(
          c.responsibleUserId && typeof c.responsibleUserId === 'object' ? c.responsibleUserId : null
        );
        // Load counterparty type for the selected counterparty
        const cpId = c.counterpartyId?._id || c.counterpartyId;
        if (cpId) {
          parametersAPI.counterparties.get(cpId)
            .then(({ data: cpData }) => {
              if (cpData.item?.counterpartyTypeId) {
                const typeId = cpData.item.counterpartyTypeId?._id || cpData.item.counterpartyTypeId;
                setSelectedCpTypeId(typeId);
              }
            })
            .catch(() => {});
        }
      }).catch(() => toast.error('Failed to load contract'));
    }
  }, [id, isEdit]);

  useEffect(() => {
    if (!isEdit) return;
    documentsAPI.list(id)
      .then(({ data }) => setExistingDocs(data.documents || []))
      .catch(() => { /* the slots simply show as empty */ });
  }, [id, isEdit]);

  // Load workflows for the fallback picker (contract type without an assigned
  // workflow template) — only All-Companies workflows plus the contract's
  // company's own may run its process.
  useEffect(() => {
    const scope = core.companyId ? { companyId: core.companyId } : {};
    workflowsAPI.list(scope).then(({ data }) => setWorkflows(data.workflows || [])).catch(() => {});
  }, [core.companyId]);

  const handleCoreChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCore(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    clearFieldError(name);
  };

  // Editing a field dismisses its validation message immediately.
  const clearFieldError = (key) => {
    setFieldErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Inline validation message under a core field.
  const FieldError = ({ k }) => (
    fieldErrors[k] ? <span className="cf-field__error">{fieldErrors[k]}</span> : null
  );

  // Shown above an occupied draft/signed slot: the document is already there,
  // and uploading again extends its version history instead of duplicating it.
  const ExistingDocNote = ({ doc }) => (
    <div className="cf-docgroup__existing">
      <FiFile size={16} className="cf-docgroup__existing-icon" />
      <div className="cf-docgroup__existing-info">
        <span className="cf-docgroup__existing-name">{doc.title}</span>
        <span className="cf-docgroup__existing-meta">
          {t('contractForm.existingDocVersion', {
            version: doc.currentVersion,
            defaultValue: `v${doc.currentVersion} · already attached`,
          })}
        </span>
      </div>
      <span className="cf-docgroup__existing-hint">
        {t('contractForm.uploadAddsVersion', {
          defaultValue: 'Uploading a file adds a new version to this document.',
        })}
      </span>
    </div>
  );

  const handleFieldChange = (fieldId, value) => {
    setFieldData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleTableChange = (sectionId, rows) => {
    setFieldData(prev => ({ ...prev, [`table_${sectionId}`]: rows }));
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) setContractFile(file);
  };

  const handleSignedFileDrop = (e) => {
    e.preventDefault();
    setSignedDragOver(false);
    if (!signedUploadEnabled) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) setSignedFile(file);
  };

  // Append files to the attachments list, de-duplicating by name+size.
  // Each staged supporting file carries its classification alongside the File
  // object: { file, docTypeId, expiresAt }.
  const addAttachments = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setAttachmentFiles((prev) => {
      const seen = new Set(prev.map((r) => `${r.file.name}:${r.file.size}`));
      const merged = [...prev];
      incoming.forEach((f) => {
        const key = `${f.name}:${f.size}`;
        if (!seen.has(key)) { seen.add(key); merged.push({ file: f, docTypeId: '', expiresAt: '' }); }
      });
      return merged;
    });
  };

  const removeAttachment = (index) => {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAttachmentsDrop = (e) => {
    e.preventDefault();
    setAttachDragOver(false);
    addAttachments(e.dataTransfer?.files);
  };

  // Apply extracted fields from template matching
  const handleTemplateApply = useCallback(async (extractedFields, fieldSchemaId) => {
    const DATE_FIELDS = ['contractDate', 'startDate', 'endDate', 'signingDate'];
    const NUMBER_FIELDS = ['contractAmount'];
    const COUNTERPARTY_FIELDS = [
      'counterpartyName', 'counterpartyAddress', 'counterpartyCity',
      'counterpartyCountry', 'counterpartyPhone', 'counterpartyEmail',
      'counterpartyContactPerson',
    ];
    const warnings = [];
    const coreUpdates = {};
    const fieldDataUpdates = {};

    // Collect counterparty fields separately — we'll try to match them to a system record
    const extractedCpFields = {};

    for (const [key, field] of Object.entries(extractedFields)) {
      if (!field.matched || !field.value) continue;

      let value = field.value;

      // Collect counterparty fields for later matching
      if (field.fieldSource === 'core' && COUNTERPARTY_FIELDS.includes(field.fieldKey)) {
        extractedCpFields[field.fieldKey] = value;
        continue; // handled below after counterparty search
      }

      // Smart date parsing — normalize any format/language to YYYY-MM-DD
      if (field.fieldSource === 'core' && DATE_FIELDS.includes(field.fieldKey)) {
        const parsed = smartParseDate(value);
        if (parsed) {
          value = parsed;
        } else {
          warnings.push(`Could not parse date "${value}" for ${field.fieldLabel}`);
          continue;
        }
      }

      // Number parsing — strip formatting (commas, spaces, currency symbols)
      if (field.fieldSource === 'core' && NUMBER_FIELDS.includes(field.fieldKey)) {
        const num = parseFormattedNumber(value);
        if (num !== null) {
          value = num;
        } else {
          warnings.push(`Could not parse number "${value}" for ${field.fieldLabel}`);
          continue;
        }
      }

      // Currency validation — check against system currencies. The display
      // name is multilang, so an extracted "Türk Lirası" has to match the
      // Turkish entry even when the UI is running in English: compare against
      // every language the currency is named in, not just the active one.
      if (field.fieldSource === 'core' && field.fieldKey === 'currency') {
        const needle = value.trim().toLowerCase();
        const matched = currencies.find(c =>
          String(c.code || '').toLowerCase() === needle ||
          localizedNames(c.name).some(n => n === needle)
        );
        if (matched) {
          value = matched.code;
        } else {
          warnings.push(`Currency "${value}" not found in the system`);
          continue;
        }
      }

      // Priority normalization
      if (field.fieldSource === 'core' && field.fieldKey === 'priority') {
        const normalized = value.trim().toLowerCase();
        const validPriorities = ['low', 'medium', 'high', 'critical'];
        if (validPriorities.includes(normalized)) {
          value = normalized;
        } else {
          warnings.push(`Priority "${value}" is not valid`);
          continue;
        }
      }

      // Confidentiality level normalization
      if (field.fieldSource === 'core' && field.fieldKey === 'confidentialityLevel') {
        const normalized = value.trim().toLowerCase();
        if (CONFIDENTIALITY_OPTIONS.some(o => o.value === normalized)) {
          value = normalized;
        } else {
          warnings.push(`Confidentiality level "${value}" is not valid`);
          continue;
        }
      }

      if (field.fieldSource === 'core') {
        coreUpdates[field.fieldKey] = value;
      } else {
        fieldDataUpdates[field.fieldKey] = value;
      }
    }

    // ── Counterparty matching: search for similar names across all types ──
    if (extractedCpFields.counterpartyName) {
      const cpName = extractedCpFields.counterpartyName.trim();
      let matchedCp = null;

      try {
        // Search across all counterparty types
        const res = await parametersAPI.counterparties.list({
          active: 'true', limit: 500,
        });
        const allCps = res.data.items || [];

        // Exact match first
        matchedCp = allCps.find(cp =>
          cp.name.toLowerCase() === cpName.toLowerCase()
        );

        // Fuzzy match: check if name contains or is contained
        if (!matchedCp) {
          const cpNameLower = cpName.toLowerCase();
          matchedCp = allCps.find(cp => {
            const n = cp.name.toLowerCase();
            return n.includes(cpNameLower) || cpNameLower.includes(n);
          });
        }

        if (matchedCp) {
          // Found — auto-fill all counterparty fields from the system record
          coreUpdates.counterpartyId = matchedCp._id;
          coreUpdates.counterpartyName = matchedCp.name;
          coreUpdates.counterpartyAddress = matchedCp.address || '';
          coreUpdates.counterpartyCity = matchedCp.city || '';
          coreUpdates.counterpartyCountry = matchedCp.country || '';
          coreUpdates.counterpartyPhone = matchedCp.phone || '';
          coreUpdates.counterpartyEmail = matchedCp.email || '';
          coreUpdates.counterpartyContactPerson = matchedCp.contactPerson || '';
          // Also set the counterparty type
          const cpTypeId = matchedCp.counterpartyTypeId?._id || matchedCp.counterpartyTypeId;
          if (cpTypeId) setSelectedCpTypeId(cpTypeId);
          toast.info(`Counterparty "${matchedCp.name}" found and linked`);
        } else {
          // Not found — skip counterparty fields entirely
          warnings.push(`Counterparty "${cpName}" not found in the system — please select manually`);
        }
      } catch {
        warnings.push(`Could not search counterparties — please select manually`);
      }
    }

    if (Object.keys(coreUpdates).length > 0) {
      setCore(prev => ({ ...prev, ...coreUpdates }));
    }
    if (Object.keys(fieldDataUpdates).length > 0) {
      setFieldData(prev => ({ ...prev, ...fieldDataUpdates }));
    }

    const totalApplied = Object.keys(coreUpdates).length + Object.keys(fieldDataUpdates).length;
    toast.success(`${totalApplied} field${totalApplied !== 1 ? 's' : ''} auto-filled from template`);
    warnings.forEach(w => toast.warn(w, { autoClose: 5000 }));
  }, [currencies, setSelectedCpTypeId]);

  // Build the API payload for one obligation row. Omit an empty owner so the
  // backend doesn't try to cast "" to an ObjectId.
  const buildObligationPayload = (ob) => {
    const p = {
      title: ob.title.trim(),
      description: ob.description || '',
      type: ob.type || 'other',
      dueDate: ob.dueDate,
      status: ob.status || 'pending',
    };
    if (ob.owner) p.owner = ob.owner;
    return p;
  };

  // Persist obligations as their own records linked to the contract. Creates new
  // rows, updates existing ones, and deletes rows the user removed (edit mode).
  // Each call is best-effort so one failure doesn't abort the whole save.
  const persistObligations = async (contractId) => {
    const valid = obligations.filter((o) => o.title && o.title.trim() && o.dueDate);
    const skipped = obligations.length - valid.length;
    if (skipped > 0) {
      toast.warn(t('contractForm.obligations.skippedIncomplete', { count: skipped, defaultValue: `${skipped} obligation(s) skipped — title and due date are required.` }));
    }
    for (const ob of valid) {
      try {
        if (ob._id) await obligationsAPI.update(ob._id, buildObligationPayload(ob));
        else await obligationsAPI.create({ contractId, ...buildObligationPayload(ob) });
      } catch (e) {
        toast.warn(t('contractForm.obligations.saveFailed', { title: ob.title, defaultValue: `Could not save obligation "${ob.title}".` }));
      }
    }
    // Deletes: original obligations no longer present among the valid rows.
    const keptIds = new Set(valid.map((o) => o._id).filter(Boolean));
    for (const oid of originalObligationIds) {
      if (!keptIds.has(oid)) {
        try { await obligationsAPI.delete(oid); } catch (e) { /* best effort */ }
      }
    }
  };

  // Required-field check applied ONLY when sending a contract onward (review,
  // approval, or completing an already-signed contract). Drafts skip this
  // entirely. Returns a list of missing field labels and stores a per-field
  // error map so each field can show its own validation message.
  const collectMissingRequired = ({ requireSigningDate = false } = {}) => {
    const missing = [];
    const errors = {};
    const requiredMsg = t('contractForm.requiredField', { defaultValue: 'This field is required' });
    const requireCore = (key, filled, label) => {
      if (!filled) {
        errors[key] = requiredMsg;
        missing.push(label);
      }
    };
    requireCore('contractName', core.contractName?.trim(), t('contractForm.contractName'));
    requireCore('contractDate', core.contractDate, t('contractForm.contractDate'));
    requireCore('companyId', core.companyId, t('contractForm.company', { defaultValue: 'Company' }));
    requireCore('contractTypeId', core.contractTypeId, t('contractForm.contractType'));
    requireCore('counterpartyName', core.counterpartyName?.trim(), t('contractForm.counterparty', { defaultValue: 'Counterparty' }));
    requireCore('responsibleDepartmentId', core.responsibleDepartmentId?.trim(), t('contractForm.responsibleDepartment'));
    requireCore('responsibleUserId', core.responsibleUserId, t('contractForm.responsiblePerson', { defaultValue: 'Responsible Person' }));
    requireCore('templateSource', core.templateSource, t('contractForm.templateSource'));
    requireCore('priority', core.priority, t('contractForm.priority'));
    requireCore('confidentialityLevel', core.confidentialityLevel, t('contractForm.confidentialityLevel'));
    requireCore('startDate', core.startDate, t('contractForm.startDate'));
    requireCore('endDate', core.endDate, t('contractForm.endDate'));
    // Signature date is only mandatory at the final stage — here that is the
    // "record an already-signed contract" path, which completes immediately.
    if (requireSigningDate) {
      requireCore('signingDate', core.signingDate, t('contractForm.signingDate'));
    }
    // End date may never precede the start date (ISO strings compare safely).
    if (core.startDate && core.endDate && core.endDate < core.startDate) {
      const msg = t('contractForm.endBeforeStart', { defaultValue: 'End date cannot be before start date' });
      errors.endDate = msg;
      missing.push(msg);
    }
    if (fieldSchema?.sections?.length) {
      for (const section of fieldSchema.sections) {
        if (section.type !== 'fields' || section.isActive === false) continue;
        const hidden = computeHiddenFields(section.fields || [], fieldData, section.id);
        for (const field of section.fields || []) {
          if (field.isActive === false) continue;
          if (!field.required || hidden.has(field.id)) continue;
          const v = fieldData[`${section.id}_${field.id}`];
          const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
          if (empty) missing.push(field.label);
        }
      }
    }
    setFieldErrors(errors);
    return missing;
  };

  // Validate, then submit. The user never picks review vs approval — the
  // contract type's assigned workflow template fully defines the path (step
  // order, passive steps skipped). Only when the type has no assigned
  // template does a workflow picker open as a fallback.
  const openSend = () => {
    // Catch the missing signed copy before anything starts.
    if (signedUploadMissing) {
      setExpandedSections(prev => ({ ...prev, file: true }));
      toast.error(t('contractForm.signedContractRequired'));
      document.getElementById('s-file')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    // The draft-document gate moved into handleSave: whether the draft is
    // required now depends on the workflow that will run (Taslak Zorunluluğu
    // may defer it to a Control step), and the picker choice isn't known yet.
    const missing = collectMissingRequired();
    if (missing.length) {
      toast.error(t('contractForm.missingRequired', {
        fields: missing.slice(0, 4).join(', '),
        defaultValue: `Please complete required fields: ${missing.slice(0, 4).join(', ')}`,
      }));
      return;
    }
    if (typeWorkflow) {
      handleSave('submit', typeWorkflow._id);
      return;
    }
    setWfSearch('');
    setWfPickerOpen(true);
  };

  // Unified save handler. action:
  //   'draft'  → save as draft, no validation
  //   'submit' → save then start the workflow template (all its active steps)
  //   'update' → save edits to a non-draft contract (existing behaviour)
  // workflowId is the contract type's template (or the fallback picker choice).
  const handleSave = async (action, workflowId = null) => {
    // Required-field gate on EVERY save — drafts included: the core fields
    // are mandatory at the contract creation stage. The signature date is
    // only added for the already-signed path, which completes immediately
    // (İmza Tarihi son aşamada zorunlu).
    {
      const missing = collectMissingRequired({ requireSigningDate: action === 'signed' });
      if (missing.length) {
        toast.error(t('contractForm.missingRequired', {
          fields: missing.slice(0, 4).join(', '),
          defaultValue: `Please complete required fields: ${missing.slice(0, 4).join(', ')}`,
        }));
        return;
      }
    }

    // A contract flagged as already signed cannot move on without its signed
    // copy. Saving a draft still works, so nothing typed so far is lost.
    if (signedUploadMissing && action !== 'draft') {
      setWfPickerOpen(false);
      setExpandedSections(prev => ({ ...prev, file: true }));
      toast.error(t('contractForm.signedContractRequired'));
      document.getElementById('s-file')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Likewise a not-yet-signed contract cannot move on without its draft
    // document (Signed = No → Draft = Required). Drafts may still be saved.
    // Exception: a workflow whose Taslak Zorunluluğu defers the draft to a
    // Control step accepts the contract without one — the workflow's own
    // Control-step gate enforces it later (mirrored server-side).
    const targetWorkflow = workflowId
      ? (workflows.find(w => w._id === workflowId) || typeWorkflow)
      : typeWorkflow;
    const draftDeferred = action === 'submit'
      && targetWorkflow?.draftRequirement?.mode === 'at_control_step';
    if (draftUploadMissing && action !== 'draft' && !draftDeferred) {
      setWfPickerOpen(false);
      setExpandedSections(prev => ({ ...prev, file: true }));
      toast.error(t('contractForm.draftDocumentRequired'));
      document.getElementById('s-file')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setWfPickerOpen(false);
    setSubmitting(true);
    setSubmitAction(action);
    try {
      // Values typed into fields a radio selection has since hidden (e.g.
      // "Yes" answered, details filled, then switched to "No") must not be
      // saved — drop them from the payload. The form state keeps them, so
      // switching back to "Yes" before leaving restores what was typed.
      const visibleFieldData = (() => {
        if (!fieldSchema?.sections?.length) return fieldData;
        const out = { ...fieldData };
        for (const section of fieldSchema.sections) {
          if (section.type !== 'fields') continue;
          const hidden = computeHiddenFields(section.fields || [], fieldData, section.id);
          hidden.forEach((fid) => { delete out[`${section.id}_${fid}`]; });
        }
        return out;
      })();
      const data = {
        ...core,
        tags: core.tags ? core.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        contractAmount: core.contractAmount ? Number(core.contractAmount) : null,
        reminderDays: core.reminderDays ? Number(core.reminderDays) : null,
        fieldData: visibleFieldData,
      };

      // Upload one of the standard contract documents (draft / signed). Each is
      // an editable, versioned contract document filed into its own folder —
      // NOT an attachment — so it shows up under the contract's Documents.
      // Returns whether the upload landed: the submit path must not move the
      // contract onward when its mandatory document never made it up.
      const uploadContractDoc = async (contractId, file, category, fallbackTitle) => {
        try {
          const docData = new FormData();
          const baseName = file.name.replace(/\.[^./\\]+$/, '');
          docData.append('title', (fallbackTitle || baseName || 'Document').trim());
          docData.append('category', category);
          docData.append('file', file);
          await documentsAPI.create(contractId, docData);
          return true;
        } catch (docErr) {
          const errData = docErr?.response?.data;
          toast.error(missingPermissionMessage(errData, t, i18n.language)
            || errData?.error || t('contractForm.documentUploadFailed'));
          return false;
        }
      };

      let savedId = id;
      let draftUploadOk = true;
      let signedUploadOk = true;
      if (isEdit) {
        await dispatch(updateContract({ id, data })).unwrap();
        if (contractFile) {
          draftUploadOk = await uploadContractDoc(id, contractFile, 'draft', core.contractName);
          // A failed upload keeps the file selected so retrying is one click.
          if (draftUploadOk) setContractFile(null);
        }
        if (signedFile && signedUploadEnabled) {
          const signedTitle = t('contractForm.signedDocTitle', {
            name: core.contractName || t('contractForm.signedContract'),
            defaultValue: `${core.contractName} (Signed)`,
          });
          signedUploadOk = await uploadContractDoc(id, signedFile, 'signed', signedTitle);
          if (signedUploadOk) setSignedFile(null);
        }
        await persistObligations(id);
      } else {
        const result = await dispatch(createContract(data)).unwrap();
        savedId = result.contract._id;

        if (contractFile) {
          draftUploadOk = await uploadContractDoc(savedId, contractFile, 'draft', core.contractName);
        }

        if (signedFile && signedUploadEnabled) {
          const signedTitle = t('contractForm.signedDocTitle', {
            name: core.contractName || t('contractForm.signedContract'),
            defaultValue: `${core.contractName} (Signed)`,
          });
          signedUploadOk = await uploadContractDoc(savedId, signedFile, 'signed', signedTitle);
        }

        // Supporting files are stored as attachments, each with its
        // classification (type + expiry date) when the user provided one.
        for (const row of attachmentFiles) {
          try {
            const attachData = new FormData();
            attachData.append('document', row.file);
            if (row.docTypeId) attachData.append('docTypeId', row.docTypeId);
            if (row.expiresAt) attachData.append('expiresAt', row.expiresAt);
            await contractsAPI.upload(savedId, attachData);
          } catch (attachErr) {
            toast.warn(t('contractForm.attachmentUploadFailed', { name: row.file.name }));
          }
        }

        // Obligations are created as their own records linked to the contract.
        await persistObligations(savedId);
      }

      // A document upload failed. The upload error was already shown — now
      // keep the contract from moving onward without its mandatory document.
      if (!draftUploadOk || !signedUploadOk) {
        if (action === 'update') {
          // Stay on the edit form: the file is still selected, retry is one click.
          return;
        }
        if (action !== 'draft') {
          // Submit/signed path: the contract is saved as a draft, but it must
          // not move on. Land on the saved draft so the upload can be retried.
          toast.warn(t('contractForm.sendFailed', { defaultValue: 'Saved as draft, but could not send it onward.' }));
          navigate(`/contracts/${savedId}/edit`);
          return;
        }
      }

      // Already-signed contracts bypass review/approval and land executed.
      if (action === 'signed') {
        try {
          // SEC-04: PUT /contracts/:id rejects every `status` except this one
          // transition — draft → fully_executed on a contract flagged as
          // already signed — and the backend only honours it when the signed
          // copy uploaded above exists and a signing date is given. Any other
          // status value is refused server-side (STATUS_NOT_ASSIGNABLE).
          await dispatch(updateContract({
            id: savedId,
            data: { status: 'fully_executed', signingDate: core.signingDate },
          })).unwrap();
        } catch (statusErr) {
          // The contract and its signed copy are saved; only the final status
          // didn't stick, so send the user to it rather than losing the work.
          toast.warn(t('contractForm.signedStatusFailed', { defaultValue: 'Contract saved, but its status could not be set to signed.' }));
          navigate(`/contracts/${savedId}`);
          return;
        }
        toast.success(t('contractForm.signedContractCreated', { defaultValue: 'Signed contract created' }));
        navigate(`/contracts/${savedId}`);
        return;
      }

      // Plain save (draft or non-draft update): done.
      if (action === 'draft' || action === 'update') {
        toast.success(action === 'update'
          ? t('contractForm.contractUpdated')
          : t('contractForm.draftSaved', { defaultValue: 'Saved as draft' }));
        navigate(action === 'update' ? `/contracts/${savedId}` : '/contracts');
        return;
      }

      // Transition the (now saved) contract onward. The save already succeeded,
      // so if the transition fails we must NOT retry the save (that would create
      // a duplicate) — land on the saved draft's edit screen so it can be retried.
      // The workflow template fully defines the path: it starts from the first
      // active step and runs every step in its defined order.
      try {
        if (action === 'submit') await workflowsAPI.start(savedId, { workflowId });
      } catch (transErr) {
        const tData = transErr?.response?.data;
        const tMsg = tData?.code === 'ORG_ASSIGNEE_UNRESOLVED'
          ? t('flows.messages.orgAssigneeUnresolved', { step: tData.stepName })
          : tData?.message || tData?.error;
        toast.error(tMsg || t('contractForm.sendFailed', { defaultValue: 'Saved as draft, but could not send it onward.' }));
        navigate(`/contracts/${savedId}/edit`);
        return;
      }

      toast.success(t('contractForm.submitted', { defaultValue: 'Contract submitted' }));
      // Land the starter on the Timeline tab so they see the flow kick off.
      // One-shot navigation state — later visits open the detail normally.
      navigate(`/contracts/${savedId}`, { state: { openTab: 'timeline' } });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || err;
      toast.error(msg || t('contractForm.saveFailed', { defaultValue: 'Failed to save contract' }));
    } finally {
      setSubmitting(false);
      setSubmitAction(null);
    }
  };

  const selectedType = contractTypes.find(ct => ct._id === core.contractTypeId);

  const accentVars = {
    '--accent': liveAccent,
    '--accent-bg': hexToRgba(liveAccent, 0.06),
    '--accent-bg-hover': hexToRgba(liveAccent, 0.10),
    '--accent-ring': hexToRgba(liveAccent, 0.10),
    '--accent-glow': hexToRgba(liveAccent, 0.25),
    '--accent-dark': liveAccent,
  };

  // ── Memoized section list for the left rail (TOC) ────────────────────────
  const steps = useMemo(() => {
    const list = [
      {
        key: 'coreIdentity',
        label: t('contractForm.stepIdentity', { defaultValue: 'General' }),
        icon: FiFileText,
        done: !!(core.contractName && core.contractTypeId && core.contractDate),
      },
      {
        key: 'counterparty',
        label: t('contractForm.stepCounterparty', { defaultValue: 'Counterparty' }),
        icon: FiBriefcase,
        done: !!core.counterpartyName,
      },
      {
        key: 'dates',
        label: t('contractForm.stepTimeline', { defaultValue: 'Timeline' }),
        icon: FiCalendar,
        done: !!(core.startDate && core.endDate),
      },
    ];
    if (fieldSchema?.sections?.length) {
      fieldSchema.sections.forEach((section) => {
        if (section.isActive === false) return; // passive sections stay hidden
        list.push({
          key: `schema_${section.id}`,
          label: section.title,
          icon: FiGrid,
          color: section.color,
          done: false,
        });
      });
    }
    list.push({
      key: 'tags',
      label: t('contractForm.stepTags', { defaultValue: 'Tags' }),
      icon: FiTag,
      done: !!(core.tags && core.tags.length > 0),
    });
    list.push({
      key: 'obligations',
      label: t('contractForm.stepObligations', { defaultValue: 'Obligations' }),
      icon: FiCheckCircle,
      done: obligations.length > 0,
    });
    if (showDocumentsSection) {
      list.push({
        key: 'file',
        label: t('contractForm.stepFile', { defaultValue: 'Contract Documents' }),
        icon: FiFileText,
        done: !!contractFile || !!signedFile,
      });
    }
    if (!isEdit) {
      list.push({
        key: 'attachments',
        label: t('contractForm.stepAttachments', { defaultValue: 'Supporting Documents' }),
        icon: FiPaperclip,
        done: attachmentFiles.length > 0,
      });
    }
    return list;
  }, [core.contractName, core.contractTypeId, core.contractDate, core.counterpartyName, core.startDate, core.endDate, core.contractAmount, core.tags, fieldSchema, contractFile, signedFile, attachmentFiles.length, obligations.length, isEdit, showDocumentsSection, t]);

  const doneCount = steps.filter((s) => s.done).length;

  return (
    <div className="cf" style={accentVars}>
      {/* ═══ Header (Swiss / minimalist) ═══ */}
      <header className="cf-head">
        <nav className="cf-head__crumbs" aria-label="Breadcrumb">
          <button type="button" className="cf-head__back" onClick={() => navigate(-1)} aria-label={t('contractForm.back', { defaultValue: 'Back' })}>
            <FiArrowLeft size={15} />
          </button>
          <button type="button" className="cf-head__crumb" onClick={() => navigate('/contracts')}>
            {t('contractForm.contractsCrumb', { defaultValue: 'Contracts' })}
          </button>
          <span className="cf-head__crumb-sep" aria-hidden="true">/</span>
          <span className="cf-head__crumb cf-head__crumb--active" aria-current="page">
            {isEdit ? t('contractForm.editCrumb', { defaultValue: 'Edit' }) : t('contractForm.newCrumb', { defaultValue: 'New' })}
          </span>
        </nav>

        <div className="cf-head__row">
          <div className="cf-head__text">
            <span className="cf-head__label">
              {isEdit
                ? (isVersionContract
                  ? t('contractForm.eyebrowVersion', {
                    defaultValue: 'Editing {{type}} V{{num}}',
                    type: versionMeta?.versionType === 'addendum'
                      ? t('contractDetail.updateTypes.addendum', { defaultValue: 'Addendum' })
                      : t('contractDetail.updateTypes.amendment', { defaultValue: 'Amendment' }),
                    num: versionMeta?.majorVersion,
                  })
                  : t('contractForm.eyebrowEdit', { defaultValue: 'Editing contract' }))
                : t('contractForm.eyebrowNew', { defaultValue: 'Draft contract' })}
            </span>
            <h1 className="cf-head__title">
              {isEdit
                ? (core.contractName || t('contractForm.editContract'))
                : t('contractForm.newContract')}
            </h1>
            <p className="cf-head__subtitle">
              {isEdit ? t('contractForm.editSubtitle') : t('contractForm.newSubtitle')}
            </p>
          </div>

          {/* Compact strip sharing the title's row, filling the space beside it */}
          <TemplateFillWidget onApply={handleTemplateApply} />
        </div>

        <div className="cf-head__meter">
          <div className="cf-head__meter-top">
            <span className="cf-head__meter-label">
              {doneCount} / {steps.length} {t('contractForm.sectionsReady', { defaultValue: 'sections ready' })}
            </span>
            <span className="cf-head__meter-value">
              <strong>{progress}</strong><span>%</span>
            </span>
          </div>
          <div className="cf-head__meter-track">
            <div className="cf-head__meter-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </header>

      <form onSubmit={(e) => e.preventDefault()} noValidate className="cf-form">
        <div className="cf-shell">
          {/* ═══ Main sections ═══ */}
          <div className="cf-sections">

          {/* ── Contract Identity ── */}
          <div className="cf-section" id="s-coreIdentity">
            <SectionHeader
              sectionKey="coreIdentity"
              title={t('contractForm.counterpartyIdentity')}
              subtitle={t('contractForm.counterpartyIdentitySubtitle')}
              icon={FiFileText}
              expanded={expandedSections.coreIdentity}
              onToggle={toggleSection}
              completed={!!(core.contractName && core.contractTypeId)}
              action={(
                <CheckboxField
                  checked={core.isSignedContract}
                  onChange={(v) => {
                    setCore(p => ({ ...p, isSignedContract: v }));
                    // Reveal the upload the moment it becomes required.
                    if (v) setExpandedSections(prev => ({ ...prev, file: true }));
                  }}
                  label={t('contractForm.signedContract')}
                />
              )}
            />
            {expandedSections.coreIdentity && (
              <div className="cf-section__body">
                <div className="cf-fields">
                  <div className="cf-field cf-field--full">
                    <label className="cf-field__label">
                      <FiFileText size={13} />
                      {t('contractForm.contractName')} <span className="cf-field__required">*</span>
                    </label>
                    <input
                      name="contractName"
                      value={core.contractName}
                      onChange={handleCoreChange}
                      placeholder={t('contractForm.contractNamePlaceholder')}
                      required
                      maxLength={300}
                    />
                    <FieldError k="contractName" />
                  </div>

                  {/* Two-column reading order (Temel Bilgiler Sıralama):
                      Name (full) → Company | Contract # → Type | Date →
                      Department | Person. */}
                  <div className="cf-field">
                    <label className="cf-field__label">
                      <FiLayers size={13} />
                      {t('contractForm.company', { defaultValue: 'Company' })} <span className="cf-field__required">*</span>
                    </label>
                    <SearchableSelect
                      value={core.companyId}
                      onChange={handleCompanyChange}
                      placeholder={t('contractForm.selectCompany', { defaultValue: 'Select company' })}
                      searchPlaceholder={t('contractForm.searchCompany', { defaultValue: 'Search company…' })}
                      required
                      disabled={isEdit && contractStatus && contractStatus !== 'draft'}
                      options={companiesList.map(c => ({ value: c._id, label: c.name }))}
                    />
                    {isEdit && contractStatus && contractStatus !== 'draft' && (
                      <span className="cf-field__hint">{t('contractForm.companyLocked', { defaultValue: 'The company can only be changed while the contract is a draft.' })}</span>
                    )}
                    <FieldError k="companyId" />
                  </div>

                  <div className="cf-field cf-field--disabled">
                    <label className="cf-field__label">
                      <FiFileText size={13} />
                      Contract #
                    </label>
                    <input value="Auto-generated" disabled />
                  </div>

                  <div className="cf-field">
                    <label className="cf-field__label">
                      <FiLayers size={13} />
                      {t('contractForm.contractType')} <span className="cf-field__required">*</span>
                    </label>
                    <SearchableSelect
                      value={core.contractTypeId}
                      onChange={(v) => { setCore(p => ({ ...p, contractTypeId: v })); clearFieldError('contractTypeId'); }}
                      placeholder={t('contractForm.selectContractType')}
                      searchPlaceholder={t('contractForm.searchContractType', { defaultValue: 'Search type…' })}
                      required
                      disabled={isVersionContract}
                      options={contractTypes.map(ct => ({ value: ct._id, label: getLocalized(ct.name, i18n.language) }))}
                    />
                    {isVersionContract && (
                      <span className="cf-field__hint">{t('contractForm.versionIdentityLocked', { defaultValue: 'Carried over from the original contract — cannot be changed on a version.' })}</span>
                    )}
                    <FieldError k="contractTypeId" />
                  </div>

                  <div className="cf-field">
                    <label className="cf-field__label">
                      <FiCalendar size={13} />
                      {t('contractForm.contractDate')} <span className="cf-field__required">*</span>
                    </label>
                    <DateField
                      value={core.contractDate}
                      onChange={(v) => { setCore(p => ({ ...p, contractDate: v })); clearFieldError('contractDate'); }}
                      required
                    />
                    <FieldError k="contractDate" />
                  </div>

                  <div className="cf-field">
                    <label className="cf-field__label">
                      {t('contractForm.responsibleDepartment')} <span className="cf-field__required">*</span>
                    </label>
                    <div className="cf-dept-search" ref={deptRef}>
                      <input
                        type="text"
                        className="cf-dept-search__input"
                        placeholder={t('contractForm.searchDepartment')}
                        value={deptDropdownOpen ? deptSearch : core.responsibleDepartmentId}
                        onFocus={() => { setDeptDropdownOpen(true); setDeptSearch(''); }}
                        onChange={(e) => setDeptSearch(e.target.value)}
                      />
                      {core.responsibleDepartmentId && !deptDropdownOpen && (
                        <button
                          type="button"
                          className="cf-dept-search__clear"
                          onClick={() => setCore(p => ({ ...p, responsibleDepartmentId: '' }))}
                        >
                          <FiX size={14} />
                        </button>
                      )}
                      {deptDropdownOpen && (
                        <div className="cf-dept-search__dropdown">
                          {filteredDepts.length > 0 ? filteredDepts.map(d => (
                            <div
                              key={d._id}
                              className={`cf-dept-search__option${core.responsibleDepartmentId === d.name ? ' cf-dept-search__option--active' : ''}`}
                              onClick={() => {
                                setCore(p => ({ ...p, responsibleDepartmentId: d.name }));
                                clearFieldError('responsibleDepartmentId');
                                setDeptDropdownOpen(false);
                                setDeptSearch('');
                              }}
                            >
                              {d.color && <span className="cf-dept-search__dot" style={{ background: d.color }} />}
                              <span>{d.name}</span>
                              {d.code && <span className="cf-dept-search__code">{d.code}</span>}
                            </div>
                          )) : (
                            <div className="cf-dept-search__empty">
                              {t('contractForm.noDepartmentsFound')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <FieldError k="responsibleDepartmentId" />
                  </div>

                  <div className="cf-field">
                    <label className="cf-field__label">
                      <FiUser size={13} />
                      {t('contractForm.responsiblePerson', { defaultValue: 'Responsible Person' })} <span className="cf-field__required">*</span>
                    </label>
                    <SearchableSelect
                      value={core.responsibleUserId || ''}
                      onChange={(v) => { handleResponsibleChange(v); clearFieldError('responsibleUserId'); }}
                      options={responsibleUserOptions}
                      placeholder={t('contractForm.selectResponsiblePerson', { defaultValue: 'Select responsible person…' })}
                      required
                    />
                    <FieldError k="responsibleUserId" />
                  </div>

                  <div className="cf-field">
                    <label className="cf-field__label">
                      <FiFile size={13} />
                      {t('contractForm.templateSource')} <span className="cf-field__required">*</span>
                    </label>
                    <SearchableSelect
                      value={core.templateSource}
                      onChange={(v) => { setCore(p => ({ ...p, templateSource: v })); clearFieldError('templateSource'); }}
                      placeholder={t('contractForm.selectTemplateSource')}
                      required
                      options={TEMPLATE_SOURCE_OPTIONS.map(o => ({ value: o.value, label: t(o.labelKey) }))}
                    />
                    <FieldError k="templateSource" />
                  </div>

                  {/* Priority + Confidentiality share one row so the two chip
                      selectors always sit side by side. */}
                  <div className="cf-fields__row">
                    <div className="cf-field">
                      <label className="cf-field__label">
                        <FiShield size={13} />
                        {t('contractForm.priority')} <span className="cf-field__required">*</span>
                      </label>
                      <ChipSelector
                        options={PRIORITY_OPTIONS}
                        value={core.priority}
                        onChange={(v) => { setCore(p => ({ ...p, priority: v })); clearFieldError('priority'); }}
                        t={t}
                      />
                      <FieldError k="priority" />
                    </div>

                    <div className="cf-field">
                      <label className="cf-field__label">
                        <FiLock size={13} />
                        {t('contractForm.confidentialityLevel')} <span className="cf-field__required">*</span>
                      </label>
                      <ChipSelector
                        options={CONFIDENTIALITY_OPTIONS}
                        value={core.confidentialityLevel}
                        onChange={(v) => { setCore(p => ({ ...p, confidentialityLevel: v })); clearFieldError('confidentialityLevel'); }}
                        t={t}
                      />
                      <FieldError k="confidentialityLevel" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Counterparty ── */}
          <div className="cf-section" id="s-counterparty">
            <SectionHeader
              sectionKey="counterparty"
              title={t('contractForm.counterpartySection')}
              subtitle={t('contractForm.counterpartySectionSubtitle')}
              icon={FiBriefcase}
              expanded={expandedSections.counterparty}
              onToggle={toggleSection}
              completed={!!core.counterpartyName}
              color="#8b5cf6"
            />
            {expandedSections.counterparty && (
              <div className="cf-section__body">
                <div className="cf-fields">
                  {/* Step 1: Select counterparty type */}
                  <div className="cf-field">
                    <label className="cf-field__label">
                      <FiBriefcase size={13} />
                      {t('contractForm.counterpartyType')} <span className="cf-field__required">*</span>
                    </label>
                    <SearchableSelect
                      value={selectedCpTypeId}
                      onChange={(v) => {
                        setSelectedCpTypeId(v);
                        // Clear selected counterparty when type changes
                        if (core.counterpartyId) handleClearCounterparty();
                      }}
                      disabled={isVersionContract}
                      placeholder={t('contractForm.selectType')}
                      searchPlaceholder={t('contractForm.searchType', { defaultValue: 'Search type…' })}
                      options={[
                        ...counterpartyTypesList.map(ct => ({
                          value: ct._id,
                          label: getLocalized(ct.name, i18n.language),
                          sublabel: ct.code,
                        })),
                        // Current-but-deactivated type: shown so the existing
                        // link stays visible; picking a different one still
                        // only offers active types.
                        ...(inactiveCpType ? [{
                          value: inactiveCpType._id,
                          label: getLocalized(inactiveCpType.name, i18n.language),
                          sublabel: `${inactiveCpType.code} — ${t('common.inactive')}`,
                        }] : []),
                      ]}
                    />
                  </div>

                  {/* Step 2: Lookup button */}
                  <div className="cf-field">
                    <label className="cf-field__label">
                      <FiSearch size={13} />
                      {t('contractForm.selectCounterparty')} <span className="cf-field__required">*</span>
                    </label>
                    <div className="cf-cp-lookup">
                      <button
                        type="button"
                        className="cf-cp-lookup__btn"
                        onClick={openCpLookup}
                        disabled={!selectedCpTypeId || isVersionContract}
                      >
                        <FiSearch size={14} />
                        {core.counterpartyName
                          ? core.counterpartyName
                          : t('contractForm.searchAndSelectCounterparty')}
                      </button>
                      {core.counterpartyName && !isVersionContract && (
                        <button type="button" className="cf-cp-lookup__clear" onClick={handleClearCounterparty}>
                          <FiX size={14} />
                        </button>
                      )}
                    </div>
                    {isVersionContract && (
                      <span className="cf-field__hint">{t('contractForm.versionIdentityLocked', { defaultValue: 'Carried over from the original contract — cannot be changed on a version.' })}</span>
                    )}
                  </div>

                  {/* Contact details — shown once type is selected, auto-filled on counterparty pick */}
                  {selectedCpTypeId && (<>
                    <div className="cf-field">
                      <label className="cf-field__label">
                        <FiUser size={13} />
                        {t('contractForm.contactPerson')}
                      </label>
                      <input
                        name="counterpartyContactPerson"
                        value={core.counterpartyContactPerson}
                        onChange={handleCoreChange}
                        placeholder={t('contractForm.contactPersonPlaceholder')}
                        maxLength={200}
                      />
                    </div>

                    <div className="cf-field">
                      <label className="cf-field__label">
                        <FiMail size={13} />
                        {t('contractForm.email')}
                      </label>
                      <input
                        name="counterpartyEmail"
                        value={core.counterpartyEmail}
                        onChange={handleCoreChange}
                        placeholder="email@example.com"
                        maxLength={150}
                        type="email"
                      />
                    </div>

                    <div className="cf-field">
                      <label className="cf-field__label">
                        <FiPhone size={13} />
                        {t('contractForm.phone')}
                      </label>
                      <input
                        name="counterpartyPhone"
                        value={core.counterpartyPhone}
                        onChange={handleCoreChange}
                        placeholder="+90 (555) 000 0000"
                        maxLength={30}
                      />
                    </div>

                    <div className="cf-field">
                      <label className="cf-field__label">
                        <FiMapPin size={13} />
                        {t('contractForm.country')}
                      </label>
                      <input
                        name="counterpartyCountry"
                        value={core.counterpartyCountry}
                        onChange={handleCoreChange}
                        maxLength={100}
                      />
                    </div>

                    <div className="cf-field">
                      <label className="cf-field__label">
                        <FiMapPin size={13} />
                        {t('contractForm.city')}
                      </label>
                      <input
                        name="counterpartyCity"
                        value={core.counterpartyCity}
                        onChange={handleCoreChange}
                        maxLength={100}
                      />
                    </div>

                    <div className="cf-field cf-field--full">
                      <label className="cf-field__label">
                        <FiMapPin size={13} />
                        {t('contractForm.address')}
                      </label>
                      <input
                        name="counterpartyAddress"
                        value={core.counterpartyAddress}
                        onChange={handleCoreChange}
                        placeholder={t('contractForm.streetAddressPlaceholder')}
                        maxLength={500}
                      />
                    </div>
                  </>)}
                </div>
              </div>
            )}
          </div>

          {/* ── Dates & Timeline ── */}
          <div className="cf-section" id="s-dates">
            <SectionHeader
              sectionKey="dates"
              title={t('contractForm.datesAndTimeline')}
              subtitle={t('contractForm.datesAndTimelineSubtitle')}
              icon={FiCalendar}
              expanded={expandedSections.dates}
              onToggle={toggleSection}
              completed={!!(core.startDate && core.endDate)}
              color="#10b981"
            />
            {expandedSections.dates && (
              <div className="cf-section__body">
                <div className="cf-fields">
                  <div className="cf-field">
                    <label className="cf-field__label">
                      {t('contractForm.startDate')} <span className="cf-field__required">*</span>
                    </label>
                    <DateField
                      value={core.startDate}
                      onChange={(v) => { setCore(p => ({ ...p, startDate: v })); clearFieldError('startDate'); clearFieldError('endDate'); }}
                      required
                    />
                    <FieldError k="startDate" />
                  </div>

                  <div className="cf-field">
                    <label className="cf-field__label">
                      {t('contractForm.endDate')} <span className="cf-field__required">*</span>
                    </label>
                    <DateField
                      value={core.endDate}
                      onChange={(v) => { setCore(p => ({ ...p, endDate: v })); clearFieldError('endDate'); }}
                      min={core.startDate || undefined}
                      required
                    />
                    <FieldError k="endDate" />
                  </div>

                  <div className="cf-field">
                    <label className="cf-field__label">
                      {t('contractForm.signingDate')}
                      {/* Only mandatory at the final stage — here that is the
                          already-signed recording path. */}
                      {core.isSignedContract && <span className="cf-field__required"> *</span>}
                    </label>
                    <DateField
                      value={core.signingDate}
                      onChange={(v) => { setCore(p => ({ ...p, signingDate: v })); clearFieldError('signingDate'); }}
                      required={core.isSignedContract}
                    />
                    <FieldError k="signingDate" />
                  </div>

                  <div className="cf-field">
                    <label className="cf-field__label">
                      <FiClock size={13} />
                      {t('contractForm.reminderDays')}
                    </label>
                    <input name="reminderDays" type="number" min="0" value={core.reminderDays} onChange={handleCoreChange} placeholder="e.g. 30" />
                  </div>

                  <div className="cf-field cf-field--full">
                    <label className="cf-field__label">
                      {t('contractForm.autoRenewal')}
                    </label>
                    <ToggleSwitch
                      checked={core.autoRenewal}
                      onChange={(v) => setCore(p => ({ ...p, autoRenewal: v }))}
                      labelOn={t('common.yes')}
                      labelOff={t('common.no')}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Financial details are not fixed form fields — they come from the
              Form Alanları Şablonu assigned to the contract type (the standard
              financial template ships a Finansal Bilgiler section). */}

          {/* ═══ Schema-Driven Custom Fields ═══ */}
          {core.contractTypeId && (
            schemaLoading ? (
              <div className="cf-section">
                <div className="cf-section__loading">
                  <div className="cf-spinner" />
                  <span>{t('contractForm.loadingFormFields')}</span>
                </div>
              </div>
            ) : !fieldSchema ? (
              <div className="cf-section">
                <div className="cf-section__empty">
                  <FiZap size={20} />
                  <span>
                    {t('contractForm.noCustomFieldsConfigured')}
                  </span>
                  <small>
                    {t('contractForm.assignFieldSchema')}
                  </small>
                </div>
              </div>
            ) : (
              fieldSchema.sections.map((section) => {
                // Passive sections (standard sections set inactive) don't render.
                if (section.isActive === false) return null;
                const sectionKey = `schema_${section.id}`;
                const isExpanded = expandedSections[sectionKey] !== false;
                const activeFields = (section.fields || []).filter((f) => f.isActive !== false);
                const isFieldsSection = section.type === 'fields';
                const hiddenFieldIds = isFieldsSection
                  ? computeHiddenFields(section.fields || [], fieldData, section.id)
                  : new Set();
                const bodyFields = isFieldsSection
                  ? activeFields.filter((f) => !hiddenFieldIds.has(f.id))
                  : [];

                return (
                  <div key={section.id} className="cf-section" id={`s-${sectionKey}`}>
                    <SectionHeader
                      sectionKey={sectionKey}
                      title={section.title}
                      expanded={isExpanded}
                      onToggle={toggleSection}
                      color={section.color}
                      icon={FiGrid}
                    />
                    {isExpanded && (bodyFields.length > 0 || !isFieldsSection) && (
                      <div className="cf-section__body">
                        {section.type === 'fields' && (
                          <div className="cf-fields">
                            {bodyFields.map(field => {
                              const fieldKey = `${section.id}_${field.id}`;
                              return (
                                <div
                                  key={field.id}
                                  className={[
                                    'cf-field',
                                    ['textarea', 'checkbox', 'radio'].includes(field.type) ? 'cf-field--full' : '',
                                    // Yes/no reads as one line: question on the
                                    // left, Evet/Hayır beside it on the right.
                                    isYesNoField(field) ? 'cf-field--inline' : '',
                                  ].filter(Boolean).join(' ')}
                                >
                                  <label className="cf-field__label">
                                    {field.label}
                                    {field.required && <span className="cf-field__required">*</span>}
                                    {field.placeholder && field.type !== 'checkbox' && field.type !== 'radio' && (
                                      <span className="cf-field__info" title={field.placeholder}>
                                        <FiInfo size={12} />
                                      </span>
                                    )}
                                  </label>
                                  <SchemaField
                                    field={field}
                                    value={fieldData[fieldKey]}
                                    onChange={(fid, val) => handleFieldChange(`${section.id}_${fid}`, val)}
                                  />
                                  {field.description && (
                                    <p className="cf-field__desc">{field.description}</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {section.type === 'table' && (
                          <SchemaTableSection
                            section={section}
                            tableRows={fieldData[`table_${section.id}`] || []}
                            onTableChange={handleTableChange}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )
          )}

          {/* ═══ Tags ═══ */}
          <div className="cf-section" id="s-tags">
            <SectionHeader
              sectionKey="tags"
              title={t('contractForm.tagsSection')}
              subtitle={t('contractForm.tagsSubtitle')}
              icon={FiTag}
              expanded={expandedSections.tags !== false}
              onToggle={toggleSection}
              color="#8b5cf6"
            />
            {expandedSections.tags !== false && (
              <div className="cf-section__body">
                <TagInput
                  value={core.tags}
                  onChange={(v) => setCore(p => ({ ...p, tags: v }))}
                />
              </div>
            )}
          </div>

          {/* ═══ Obligations ═══ */}
          <div className="cf-section" id="s-obligations">
            <SectionHeader
              sectionKey="obligations"
              title={t('contractForm.obligations.sectionTitle', { defaultValue: 'Obligations' })}
              subtitle={t('contractForm.obligations.sectionSubtitle', { defaultValue: 'Track deliverables, payments and deadlines (optional)' })}
              icon={FiCheckCircle}
              expanded={expandedSections.obligations !== false}
              onToggle={toggleSection}
              badge={obligations.length || undefined}
              color="#10b981"
            />
            {expandedSections.obligations !== false && (
              <div className="cf-section__body">
                <ObligationEditor obligations={obligations} onChange={setObligations} users={users} t={t} />
              </div>
            )}
          </div>

          {/* ═══ Contract Documents — draft + signed ═══ */}
          {showDocumentsSection && (
          <div className="cf-section" id="s-file">
            <SectionHeader
              sectionKey="file"
              title={t('contractForm.contractDocuments')}
              subtitle={t('contractForm.contractDocumentsSubtitle')}
              icon={FiFileText}
              expanded={expandedSections.file !== false}
              onToggle={toggleSection}
              color="#06b6d4"
              action={canBrowseTemplates ? (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => setTemplatePickerOpen(true)}
                >
                  <FiBookOpen size={13} /> {t('templateLibrary.browseTemplates')}
                </button>
              ) : undefined}
            />
            {expandedSections.file !== false && (
              <div className="cf-section__body">
                <div className="cf-docgroups">

                  {/* ── Draft contract — available from the very first step ── */}
                  {/* When the contract type's workflow defers the draft to a
                      Control step (Taslak Zorunluluğu), it is not required at
                      this stage — the workflow enforces it at that step. */}
                  <div className="cf-docgroup">
                    <div className="cf-docgroup__head">
                      <span className="cf-docgroup__title">
                        <FiFileText size={13} />
                        {t('contractForm.draftContract')}
                        {draftUploadRequired
                          && typeWorkflow?.draftRequirement?.mode !== 'at_control_step'
                          && <span className="cf-field__required">*</span>}
                      </span>
                      <span className="cf-docgroup__hint">
                        {draftUploadRequired
                          ? (typeWorkflow?.draftRequirement?.mode === 'at_control_step'
                            ? t('contractForm.draftContractDeferredHint')
                            : t('contractForm.draftContractRequiredHint'))
                          : t('contractForm.draftContractHint')}
                      </span>
                    </div>
                    {existingDraftDoc && <ExistingDocNote doc={existingDraftDoc} />}
                    {contractFile ? (
                      <div className="cf-file-preview">
                        <div className="cf-file-preview__icon">
                          <FiFile size={20} />
                        </div>
                        <div className="cf-file-preview__info">
                          <span className="cf-file-preview__name">{contractFile.name}</span>
                          <span className="cf-file-preview__size">{(contractFile.size / 1024).toFixed(1)} KB</span>
                        </div>
                        <button
                          type="button"
                          className="cf-file-preview__remove"
                          onClick={() => { setContractFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                        >
                          <FiX size={16} />
                        </button>
                      </div>
                    ) : (
                      <label
                        className={`cf-upload ${dragOver ? 'cf-upload--active' : ''}`}
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={handleFileDrop}
                      >
                        <div className="cf-upload__icon">
                          <FiUpload size={24} />
                        </div>
                        <span className="cf-upload__text">
                          {t('contractForm.dragDropOrBrowse')}
                        </span>
                        <span className="cf-upload__hint">{t('contractForm.documentUploadHint', { defaultValue: 'DOC, DOCX' })}</span>
                        <input
                          ref={fileInputRef}
                          type="file"
                          style={{ display: 'none' }}
                          onChange={e => { if (e.target.files[0]) setContractFile(e.target.files[0]); e.target.value = ''; }}
                          accept=".doc,.docx"
                        />
                      </label>
                    )}
                  </div>

                  {/* ── Signed contract — unlocks at the signature stage ── */}
                  <div className={`cf-docgroup${signedUploadEnabled ? '' : ' cf-docgroup--locked'}${signedUploadMissing ? ' cf-docgroup--required' : ''}`}>
                    <div className="cf-docgroup__head">
                      <span className="cf-docgroup__title">
                        {signedUploadEnabled ? <FiCheckCircle size={13} /> : <FiLock size={13} />}
                        {t('contractForm.signedContract')}
                        {signedUploadRequired && <span className="cf-field__required">*</span>}
                      </span>
                      <span className="cf-docgroup__hint">
                        {signedUploadMissing
                          ? t('contractForm.signedContractRequired')
                          : signedUploadEnabled
                            ? t('contractForm.signedContractHint')
                            : t('contractForm.signedContractLocked')}
                      </span>
                    </div>
                    {existingSignedDoc && <ExistingDocNote doc={existingSignedDoc} />}
                    {signedFile ? (
                      <div className="cf-file-preview">
                        <div className="cf-file-preview__icon">
                          <FiFile size={20} />
                        </div>
                        <div className="cf-file-preview__info">
                          <span className="cf-file-preview__name">{signedFile.name}</span>
                          <span className="cf-file-preview__size">{(signedFile.size / 1024).toFixed(1)} KB</span>
                        </div>
                        <button
                          type="button"
                          className="cf-file-preview__remove"
                          onClick={() => { setSignedFile(null); if (signedInputRef.current) signedInputRef.current.value = ''; }}
                        >
                          <FiX size={16} />
                        </button>
                      </div>
                    ) : (
                      <label
                        className={`cf-upload${signedDragOver ? ' cf-upload--active' : ''}${signedUploadEnabled ? '' : ' cf-upload--disabled'}`}
                        aria-disabled={!signedUploadEnabled}
                        onDragOver={(e) => { e.preventDefault(); if (signedUploadEnabled) setSignedDragOver(true); }}
                        onDragLeave={() => setSignedDragOver(false)}
                        onDrop={handleSignedFileDrop}
                      >
                        <div className="cf-upload__icon">
                          {signedUploadEnabled ? <FiUpload size={24} /> : <FiLock size={22} />}
                        </div>
                        <span className="cf-upload__text">
                          {signedUploadEnabled
                            ? t('contractForm.dragDropOrBrowse')
                            : t('contractForm.signedContractLockedShort')}
                        </span>
                        <span className="cf-upload__hint">{t('contractForm.signedUploadHint', { defaultValue: 'PDF, DOC, DOCX' })}</span>
                        <input
                          ref={signedInputRef}
                          type="file"
                          style={{ display: 'none' }}
                          disabled={!signedUploadEnabled}
                          onChange={e => { if (e.target.files[0]) setSignedFile(e.target.files[0]); e.target.value = ''; }}
                          accept=".pdf,.doc,.docx"
                        />
                      </label>
                    )}
                  </div>

                </div>
              </div>
            )}
          </div>
          )}

          {/* ═══ Attachments ═══ */}
          {!isEdit && (
            <div className="cf-section" id="s-attachments">
              <SectionHeader
                sectionKey="attachments"
                title={t('contractForm.attachmentsUpload', { defaultValue: 'Supporting Documents' })}
                subtitle={t('contractForm.attachmentsUploadSubtitle', { defaultValue: 'Add supporting files (optional)' })}
                icon={FiPaperclip}
                expanded={expandedSections.attachments !== false}
                onToggle={toggleSection}
                badge={attachmentFiles.length || undefined}
                color="#8b5cf6"
              />
              {expandedSections.attachments !== false && (
                <div className="cf-section__body">
                  {attachmentFiles.length > 0 && (
                    <div className="cf-file-list">
                      {attachmentFiles.map((row, idx) => (
                        <div className="cf-file-preview cf-file-preview--stacked" key={`${row.file.name}-${idx}`}>
                          <div className="cf-file-preview__row">
                            <div className="cf-file-preview__icon">
                              <FiFile size={20} />
                            </div>
                            <div className="cf-file-preview__info">
                              <span className="cf-file-preview__name">{row.file.name}</span>
                              <span className="cf-file-preview__size">{(row.file.size / 1024).toFixed(1)} KB</span>
                            </div>
                            <button
                              type="button"
                              className="cf-file-preview__remove"
                              onClick={() => removeAttachment(idx)}
                            >
                              <FiX size={16} />
                            </button>
                          </div>
                          <div className="cf-file-preview__fields">
                            <div className="cf-field">
                              <label className="cf-field__label">
                                {t('contractForm.supportingDocType', { defaultValue: 'Supporting document type' })}
                              </label>
                              <SearchableSelect
                                value={row.docTypeId}
                                onChange={(v) => setAttachmentFiles((prev) => prev.map((r, i) => (i === idx ? { ...r, docTypeId: v } : r)))}
                                placeholder={t('contractForm.selectSupportingDocType', { defaultValue: 'Select type…' })}
                                // The type's code is internal bookkeeping — the picker shows the name only.
                                options={supportingDocTypesList.map((dt) => ({ value: dt._id, label: getLocalized(dt.name, i18n.language) }))}
                              />
                            </div>
                            <div className="cf-field">
                              <label className="cf-field__label">
                                {t('contractForm.supportingExpiresAt', { defaultValue: 'Expiry date' })}
                              </label>
                              <DateField
                                value={row.expiresAt}
                                onChange={(v) => setAttachmentFiles((prev) => prev.map((r, i) => (i === idx ? { ...r, expiresAt: v } : r)))}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <label
                    className={`cf-upload ${attachDragOver ? 'cf-upload--active' : ''}`}
                    onDragOver={(e) => { e.preventDefault(); setAttachDragOver(true); }}
                    onDragLeave={() => setAttachDragOver(false)}
                    onDrop={handleAttachmentsDrop}
                  >
                    <div className="cf-upload__icon">
                      <FiPaperclip size={24} />
                    </div>
                    <span className="cf-upload__text">
                      {attachmentFiles.length > 0
                        ? t('contractForm.addMoreAttachments', { defaultValue: 'Add more attachments' })
                        : t('contractForm.dragDropOrBrowse')}
                    </span>
                    <span className="cf-upload__hint">PDF, DOC, DOCX, TXT, PNG, JPG</span>
                    <input
                      ref={attachInputRef}
                      type="file"
                      multiple
                      style={{ display: 'none' }}
                      onChange={e => { addAttachments(e.target.files); e.target.value = ''; }}
                      accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                    />
                  </label>
                </div>
              )}
            </div>
          )}
          </div>
        </div>

        {/* ═══ Sticky bottom action dock ═══ */}
        <div className="cf-dock" role="toolbar" aria-label={t('contractForm.actions', { defaultValue: 'Form actions' })}>
          <div className="cf-dock__inner">
            <div className="cf-dock__summary">
              <div className="cf-dock__progress">
                <span className="cf-dock__progress-num">{progress}<i>%</i></span>
                <div className="cf-dock__progress-meta">
                  <span className="cf-dock__progress-label">
                    {doneCount}/{steps.length} {t('contractForm.sectionsReady', { defaultValue: 'sections ready' })}
                  </span>
                  <div className="cf-dock__progress-track">
                    <div className="cf-dock__progress-fill" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              </div>
              <div className="cf-dock__chips">
                {core.counterpartyName && (
                  <span className="cf-dock__chip" title={core.counterpartyName}>
                    <FiBriefcase size={12} />
                    <span className="cf-dock__chip-text">{core.counterpartyName}</span>
                  </span>
                )}
                {core.startDate && core.endDate && (
                  <span className="cf-dock__chip">
                    <FiCalendar size={12} />
                    {core.startDate} → {core.endDate}
                  </span>
                )}
                {core.contractAmount && (
                  <span className="cf-dock__chip cf-dock__chip--accent">
                    <FiDollarSign size={12} />
                    {formatMoney(core.contractAmount)} {core.currency || ''}
                  </span>
                )}
              </div>
            </div>

            <div className="cf-dock__actions">
              <button type="button" className="cf-dock__cancel" onClick={() => navigate(-1)}>
                {t('common.cancel')}
              </button>

              {/* An already-signed contract has nothing to review or approve —
                  it is recorded in one action and lands fully executed. */}
              {core.isSignedContract && (!isEdit || contractStatus === 'draft') ? (
                <button
                  type="button"
                  className="cf-dock__submit"
                  disabled={submitting}
                  onClick={() => handleSave('signed')}
                >
                  {submitting && submitAction === 'signed' ? (
                    <><div className="cf-spinner cf-spinner--sm" /><span>{t('contractForm.saving')}</span></>
                  ) : (
                    <><FiCheckCircle size={15} /><span>{t('contractForm.createSignedContract', { defaultValue: 'Create Contract' })}</span></>
                  )}
                </button>
              ) : (!isEdit || contractStatus === 'draft') ? (
                <>
                  <button
                    type="button"
                    className="cf-dock__draft"
                    disabled={submitting}
                    onClick={() => handleSave('draft')}
                  >
                    {submitting && submitAction === 'draft' ? (
                      <><div className="cf-spinner cf-spinner--sm" /><span>{t('contractForm.saving')}</span></>
                    ) : (
                      <><FiSave size={15} /><span>{t('contractForm.saveDraft', { defaultValue: 'Save as Draft' })}</span></>
                    )}
                  </button>
                  <button
                    type="button"
                    className="cf-dock__submit"
                    disabled={submitting}
                    onClick={openSend}
                  >
                    {submitting && submitAction === 'submit' ? (
                      <><div className="cf-spinner cf-spinner--sm" /><span>{t('contractForm.saving')}</span></>
                    ) : (
                      <><FiSend size={15} /><span>{t('contractForm.submit', { defaultValue: 'Gönder' })}</span></>
                    )}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="cf-dock__submit"
                  disabled={submitting}
                  onClick={() => handleSave('update')}
                >
                  {submitting ? (
                    <><div className="cf-spinner cf-spinner--sm" /><span>{t('contractForm.saving')}</span></>
                  ) : (
                    <><FiSave size={15} /><span>{t('contractForm.updateContractBtn')}</span></>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </form>

      {/* ═══ Counterparty Lookup — command-palette picker ═══ */}
      {cpLookupOpen && (
        <div className="cf-pick-overlay" onClick={() => setCpLookupOpen(false)}>
          <div className="cf-pick" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            {/* Search-first header */}
            <div className="cf-pick__search">
              <FiSearch className="cf-pick__search-icon" size={18} />
              <input
                type="text"
                className="cf-pick__search-input"
                placeholder={t('contractForm.searchAllFields')}
                value={cpLookupGlobalSearch}
                onChange={(e) => setCpLookupGlobalSearch(e.target.value)}
                autoFocus
              />
              {cpLookupGlobalSearch && (
                <button type="button" className="cf-pick__search-clear" onClick={() => setCpLookupGlobalSearch('')} aria-label={t('common.cancel')}>
                  <FiX size={14} />
                </button>
              )}
              <span className="cf-pick__count">{filteredCpLookup.length}</span>
              <button className="cf-pick__close" onClick={() => setCpLookupOpen(false)} aria-label={t('common.cancel')}>
                <FiX size={18} />
              </button>
            </div>

            {/* Results */}
            <div className="cf-pick__results" ref={cpListRef}>
              {cpLookupLoading ? (
                <div className="cf-pick__state">
                  <div className="cf-spinner" />
                  <span>{t('contractForm.loading')}</span>
                </div>
              ) : filteredCpLookup.length === 0 ? (
                <div className="cf-pick__state cf-pick__state--empty">
                  <div className="cf-pick__state-art"><FiBriefcase size={26} /></div>
                  <span>{t('contractForm.noCounterpartiesFound')}</span>
                  <small>{t('contractForm.tryAdjustingSearch')}</small>
                </div>
              ) : (
                filteredCpLookup.map((cp, i) => {
                  const isSelected = core.counterpartyId === cp._id;
                  const isActive = i === cpActiveIndex;
                  const meta = [
                    cp.city && cp.country ? `${cp.city}, ${cp.country}` : (cp.city || cp.country),
                    cp.contactPerson,
                  ].filter(Boolean);
                  return (
                    <button
                      key={cp._id}
                      type="button"
                      data-cp-active={isActive}
                      className={`cf-pick__row${isActive ? ' cf-pick__row--active' : ''}${isSelected ? ' cf-pick__row--selected' : ''}`}
                      onMouseMove={() => setCpActiveIndex(i)}
                      onClick={() => handleSelectCounterparty(cp)}
                    >
                      <span className="cf-pick__avatar">{cpInitials(cp.name)}</span>
                      <span className="cf-pick__body">
                        <span className="cf-pick__line">
                          <span className="cf-pick__name">{cp.name}</span>
                          {cp.code && <span className="cf-pick__code">{cp.code}</span>}
                        </span>
                        <span className="cf-pick__meta">
                          {meta.length > 0 ? meta.map((m, mi) => (
                            <React.Fragment key={mi}>
                              {mi > 0 && <span className="cf-pick__meta-dot" />}
                              <span className="cf-pick__meta-item">
                                {mi === 0 && (cp.city || cp.country) ? <FiMapPin size={11} /> : <FiUser size={11} />}
                                {m}
                              </span>
                            </React.Fragment>
                          )) : (
                            <span className="cf-pick__meta-item cf-pick__meta-item--muted">{cp.email || cp.phone || '—'}</span>
                          )}
                        </span>
                      </span>
                      <span className="cf-pick__contact">
                        {cp.email && <span className="cf-pick__contact-item"><FiMail size={12} /> {cp.email}</span>}
                        {cp.phone && <span className="cf-pick__contact-item"><FiPhone size={12} /> {cp.phone}</span>}
                      </span>
                      <span className="cf-pick__cue" aria-hidden="true">
                        {isSelected ? <FiCheck size={16} /> : <FiCornerDownLeft size={15} />}
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer with keyboard hints */}
            <div className="cf-pick__foot">
              <div className="cf-pick__hints">
                <span className="cf-pick__hint"><kbd>↑</kbd><kbd>↓</kbd> {t('contractForm.navigate', { defaultValue: 'navigate' })}</span>
                <span className="cf-pick__hint"><kbd>↵</kbd> {t('contractForm.select', { defaultValue: 'select' })}</span>
                <span className="cf-pick__hint"><kbd>esc</kbd> {t('contractForm.close', { defaultValue: 'close' })}</span>
              </div>
              <button type="button" className="cf-pick__cancel" onClick={() => setCpLookupOpen(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Workflow picker — fallback when the contract type has no template ═══ */}
      {wfPickerOpen && (() => {
        const available = workflows;
        const q = wfSearch.trim().toLowerCase();
        const shown = q
          ? available.filter((wf) =>
              localizeSeeded(wf.name, i18n.language).toLowerCase().includes(q) ||
              localizeSeeded(wf.description, i18n.language).toLowerCase().includes(q))
          : available;
        return (
          <div className="cf-pick-overlay" onClick={() => setWfPickerOpen(false)}>
            <div className="cf-wfpick" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <div className="cf-wfpick__head">
                <div className="cf-wfpick__head-text">
                  <h3>{t('contractForm.selectWorkflow', { defaultValue: 'Select workflow' })}</h3>
                  <p>{t('contractForm.selectWorkflowDesc', { defaultValue: 'Choose the workflow this contract will follow.' })}</p>
                </div>
                <button type="button" className="cf-wfpick__close" onClick={() => setWfPickerOpen(false)} aria-label={t('common.cancel')}>
                  <FiX size={18} />
                </button>
              </div>

              {available.length > 0 && (
                <div className="cf-wfpick__search">
                  <FiSearch size={15} className="cf-wfpick__search-icon" />
                  <input
                    className="cf-wfpick__search-input"
                    placeholder={t('contractForm.searchWorkflows', { defaultValue: 'Search workflows…' })}
                    value={wfSearch}
                    onChange={(e) => setWfSearch(e.target.value)}
                    autoFocus
                  />
                </div>
              )}

              <div className="cf-wfpick__list">
                {available.length === 0 ? (
                  <div className="cf-wfpick__empty">
                    <FiSend size={26} />
                    <p>{t('contractForm.noWorkflows', { defaultValue: 'No workflows configured yet.' })}</p>
                    <button type="button" className="cf-wfpick__link" onClick={() => navigate('/flow-management')}>
                      {t('contractForm.createWorkflowFirst', { defaultValue: 'Create a workflow' })}
                    </button>
                  </div>
                ) : shown.length === 0 ? (
                  <div className="cf-wfpick__empty">
                    <FiSearch size={22} />
                    <p>{t('contractForm.noWorkflowMatches', { defaultValue: 'No workflows match your search.' })}</p>
                  </div>
                ) : (
                  shown.map((wf) => {
                    const steps = wf.steps || [];
                    return (
                      <button
                        type="button"
                        key={wf._id}
                        className="cf-wfpick__option"
                        disabled={submitting}
                        onClick={() => handleSave('submit', wf._id)}
                      >
                        <span className="cf-wfpick__opt-icon"><FiSend size={16} /></span>
                        <span className="cf-wfpick__opt-text">
                          <span className="cf-wfpick__opt-name">{localizeSeeded(wf.name, i18n.language)}</span>
                          <span className="cf-wfpick__opt-meta">
                            {t('contractForm.stepCount', { count: steps.length, defaultValue: `${steps.length} steps` })}
                          </span>
                        </span>
                        <FiChevronRight size={16} className="cf-wfpick__opt-cue" />
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ Template Library picker — templates for the selected company + type ═══ */}
      <TemplateLibraryPicker
        open={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        companyId={core.companyId}
        contractTypeId={core.contractTypeId}
      />

    </div>
  );
}

export default ContractForm;
